/** Native Lists grid — AG-Grid Community (Client-Side Row Model) over one list.
 *
 * The hand-rolled M0/M1 table is replaced by AG-Grid on the vendor-neutral getRows contract: we
 * fetch the caller's entitled entry set ONCE (server enforces scoping + leak-safe count) and AG-Grid
 * does sort / filter / column layout locally — fine at IOT scale. Delivers the Affinity-grade column
 * UX the operator asked for: drag resize / reorder, pin/freeze (column menu), a custom multi-select
 * "set" filter (the built-in set filter is Enterprise), quick-filter search, and saved + shareable
 * views (full column state + filter model round-tripped through crm_view).
 *
 * Deferred (honest scope): ROW GROUPING/AGGREGATION is AG-Grid Enterprise — supported server-side
 * (getRows group mode) but not in this Community grid. INLINE EDITING needs the M3 write-back path
 * (edits must reach Affinity or the daily re-import clobbers them), so cells are read-only here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellValueChangedEvent,
  type ColDef,
  type ColumnState,
  type GridApi,
  type ICellRendererParams,
} from 'ag-grid-community';
import { AgGridReact, useGridFilter, type CustomFilterProps } from 'ag-grid-react';
import styles from './crm.module.css';
import {
  useAcceptProposal,
  useAcceptRecord,
  useAddEntry,
  useAddField,
  useAddMember,
  useConvertFieldType,
  useCreateView,
  useCrmGrid,
  useCrmListOptions,
  useCrmListViews,
  useCrmRecordLayout,
  useDeleteEntry,
  useDeleteField,
  useDeleteOption,
  useDeleteView,
  useDismissProposal,
  useDismissRecord,
  useMemberLookup,
  useRenameOption,
  useSetFieldConfig,
  useSetFieldOptions,
  useSetListDerived,
  useSetFieldValue,
  useSetTeamDefaultView,
  useUpdateField,
  useUpdateView,
  fetchFieldUsage,
} from '../../hooks/useCrm';
import { useChromeless } from '../../hooks/useChromeless';
import { useViewportFill } from '../../hooks/useViewportFill';
import type {
  CrmGridColumn, CrmRosterOption, CrmStageLadders, CrmView, DealDismissReason, FieldDismissReason,
} from '../../types';
import { DismissReasonModal } from '../pages/DismissReasonModal';
import { CrmChecklistEditor } from './CrmChecklistEditor';
import { RefCellEditor } from './CrmRefCellEditor';
import { CrmRosterPicker } from './CrmRosterPicker';
// PR3 item 7: the roster flag is defined ONCE (crmEditability) and read by both surfaces — the grid's
// local type sets are a pre-existing duplication this deliberately does not extend.
import { ROSTER_REF_SOURCE } from './crmEditability';
// plan 1b: the deal_type-contingent Stage ladder. The SAME module the record panel filters
// through, so the two surfaces cannot offer different ladders for the same record.
import { dealTypeOf, stageLadderOptions } from './stageLadder';
import { CrmColumnHeader, reorderColIds, type EditField, type HeaderActions } from './CrmColumnHeader';
import { CrmColumnsPanel } from './CrmColumnsPanel';
import { CrmFieldEditPanel } from './CrmFieldEditPanel';
import { CrmOptionsEditor } from './CrmOptionsEditor';
import { CrmFilterPanel } from './CrmFilterPanel';
import { CrmSortPanel } from './CrmSortPanel';
import { countConditions, evaluateFilterTree, type FilterGroup } from './crmFilterTree';
import { readSortModel, sortModelColumnState, type SortLevel } from './crmSortModel';
import { CrmAiUpdatesControl } from './CrmAiUpdatesControl';
import { CrmPopover } from './CrmPopover';
import { CrmRecordPanelContainer } from './CrmRecordPanelContainer';
import { CrmShareDialog } from './CrmShareDialog';
import { CrmSidePanel } from './CrmSidePanel';
import { CrmViewSaveModal } from './CrmViewSaveModal';
import { CrmViewsPanel } from './CrmViewsPanel';
import {
  cohereAiViewState, viewConfigKey, readProposalsFilter, readValueMode, resolveDefaultView,
  type ProposalsFilter,
} from './crmViewConfig';
import {
  displayValueForMode, cellProposal, showsProposedValue, multiValueDiff,
  type MultiValueDiffEntry, type ValueMode,
} from './crmValueMode';
import {
  AI_PROPOSED_ROW_FIELD, AI_PROPOSED_ROW_VALUE, CURRENT_ROW_VALUE,
  isProposedRecordRow, withAiProposedRowColumn, withAiProposedRowValues,
} from './crmAiProposedRow';
import {
  OPEN_AI_UPDATE_COUNT_FIELD, SYSTEM_NULL_DISPLAY,
  enumDisplay, isComputedColumn, systemCellTooltip, systemCoverageFor,
} from './crmSystemFields';
import { buildRowClassRules, makeActiveRowSync } from './crmActiveRow';
import { useFirstFrameHold } from './crmFirstFrameHold';
import { useRecordKeyNav } from './crmRecordKeyNav';
import { useRecordSelection } from './crmRecordSelection';
import { CATCHALL_FIELD_KEYS } from './crmEntityRoles';
import { fmtRelativeDay, fmtShortDate } from './crmFormat';
import { cellText, cellValues } from './crmCells';
import { EntityRefs } from './EntityRefs';

ModuleRegistry.registerModules([AllCommunityModule]);

// Ember-tokened theme (Theming API — no global CSS import). Mirrors styles/theme.ts.
const emberTheme = themeQuartz.withParams({
  accentColor: '#EE6F5C',
  fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
  headerFontFamily: "'DM Mono', monospace",
  headerFontSize: 11,
  fontSize: 13,
  headerBackgroundColor: '#F8F8F6',
  borderColor: '#E5E5E0',
  foregroundColor: '#1A1A1A',
  headerTextColor: '#555555',
  // §5.7(b): an ACCEPTED (committed) row is white; hover → very light BLUE (owner 2026-08-05, which
  // replaced the grey this used to be). `oddRowBackgroundColor` already suppresses quartz's striping,
  // so "white" is the resting state for every committed row.
  // ⚠ This param alone is not the hover — it paints the ROW, and the pinned-left rail's opaque cell
  // backdrop hides it there. `crm.module.css` repaints it on the CELLS; both must carry the same value
  // or a hovered row changes colour at the pinned boundary.
  rowHoverColor: '#EFF6FD',
  oddRowBackgroundColor: '#FFFFFF',
});

const FETCH_CAP = 5000; // client-side row model ceiling (IOT scale); Enterprise SSRM is the 60k seam
const NEW_ROW_ID = '__new_row__'; // Slice 13: the pinned blank "add a record" row's sentinel id
const REF_TYPES = new Set(['person', 'person_multi', 'organization', 'organization_multi']);
const NUMERIC_TYPES = new Set(['number', 'currency']);
// inline-editable scalar types. ranked_enum (the IOT stage) is editable ONLY when the options
// endpoint supplies its vocabulary (M3.3) — it then gets a select editor, and the push validates
// the label server-side before it can reach Affinity.
const EDITABLE_TYPES = new Set(['text', 'long_text', 'number', 'currency', 'date', 'enum']);
const CELL_EDITORS: Record<string, string> = {
  text: 'agTextCellEditor', enum: 'agTextCellEditor', date: 'agTextCellEditor',
  long_text: 'agLargeTextCellEditor', number: 'agNumberCellEditor', currency: 'agNumberCellEditor',
};
// field types offered when adding a column to a native list
const ADD_COLUMN_TYPES = ['text', 'long_text', 'number', 'currency', 'date', 'enum', 'multi_enum', 'ranked_enum'];
// enum-family types carry an option picklist (M2 config_json.options).
const OPTION_TYPES = new Set(['enum', 'multi_enum', 'ranked_enum']);
// options textarea → clean ordered list, MIRRORING the backend _normalize_options exactly (so the
// FE preview + dedup match what the server stores): split on newlines/commas, trim, truncate each
// to 200 chars, drop blanks, dedupe preserving order. (The ≤200-COUNT cap is left to the backend,
// which 400s with a clear message the Save/Add handlers surface — better UX than silently dropping
// the operator's extra options.)
export const parseOptions = (text: string): string[] => {
  const out: string[] = []; const seen = new Set<string>();
  for (const raw of text.split(/[\n,]/)) {
    const s = raw.trim().slice(0, 200);
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  }
  return out;
};

interface Props {
  listId: number;
  onBack: () => void;
  openPerson: (id: number) => void;
  openOrg: (id: number) => void;
  openMsg: (id: number) => void;  // D6: threaded to the record panel's Timeline sub-view (open-email flow)
  openNote: (id: number) => void; // E4: the latest_note cell → the note overlay (the `note` param)
}


// ── cell renderers ──────────────────────────────────────────────────────────────────────────
function RefsRenderer(params: ICellRendererParams) {
  const v = displayValueForMode(params.data, params.colDef?.field, params.value, params.context?.valueMode);
  const ctx = params.context as { openPerson?: (id: number) => void; openOrganization?: (id: number) => void };
  const isPerson = params.colDef?.cellRendererParams?.kind === 'person';
  const open = isPerson ? ctx.openPerson : ctx.openOrganization; // person + org both click through (E6.2/E6.3)
  // §15 item 1: the catch-all columns' chips open the profile in a NEW TAB. On the grid you are
  // scanning a board and navigating away in place loses your place in it — the same reason the panel
  // does this. Every other ref column keeps in-place navigation.
  const newTab = CATCHALL_FIELD_KEYS.has(params.colDef?.field ?? '');
  // E2c: ONE governed renderer for every surface. A ref shows its governed NAME or it does not show
  // — the old `name || email || '#'+id` fallback printed the exact id an L1/class-ceiling viewer is
  // refused, and an email E2b-2 stopped writing for that reason.
  return (
    <EntityRefs value={v} kind={isPerson ? 'person' : 'organization'}
                onOpen={newTab ? undefined : open} newTab={newTab} />
  );
}

/**
 * §5.9 / P12: in AI mode, a multi-valued cell renders the DIFF, not a wholesale replacement — added
 * values green, removed values struck, unchanged values left alone. Returns null when there is nothing
 * to diff (not AI mode, or no proposal on this cell), so the caller falls through to its plain render.
 *
 * Shared by both multi-value renderers on purpose: `ChipsRenderer` and `PillRenderer` draw the same
 * `multi_enum` data in two different skins, and a rule applied to one of them is the exact defect
 * shape §6 records.
 */
function multiValueDiffFor(params: ICellRendererParams): MultiValueDiffEntry[] | null {
  if (params.context?.valueMode !== 'ai') return null;
  const prop = cellProposal(params.data, params.colDef?.field);
  if (!prop) return null;
  return multiValueDiff(params.value, prop.value);
}

const DIFF_CLASS: Record<MultiValueDiffEntry['state'], string> = {
  added: styles.multiAdded,
  removed: styles.multiRemoved,
  unchanged: '',
};

function ChipsRenderer(params: ICellRendererParams) {
  const diff = multiValueDiffFor(params);
  if (diff) {
    if (diff.length === 0) return <span className={styles.gridEmpty}>—</span>;
    return (
      <span className={styles.gridChips}>
        {diff.map((d, i) => (
          <span key={i} className={`${styles.chip} ${styles.chipMuted} ${DIFF_CLASS[d.state]}`}>
            {d.value}
          </span>
        ))}
      </span>
    );
  }
  const v = displayValueForMode(params.data, params.colDef?.field, params.value, params.context?.valueMode);
  if (!Array.isArray(v) || v.length === 0) return <span className={styles.gridEmpty}>—</span>;
  return (
    <span className={styles.gridChips}>
      {v.map((x, i) => <span key={i} className={`${styles.chip} ${styles.chipMuted}`}>{String(x)}</span>)}
    </span>
  );
}

// Slice 10: semantic color token → the CSS pill class (each mirrors a styles/theme.ts badge triad).
const PILL_CLASS: Record<string, string> = {
  green: styles.pillGreen, blue: styles.pillBlue, grey: styles.pillGrey,
  orange: styles.pillOrange, red: styles.pillRed, purple: styles.pillPurple,
};

/** {lowercased value → color token} from a column's structured groups (Slice 10). */
export function colorMapFromGroups(groups: CrmGridColumn['groups']): Record<string, string> {
  const map: Record<string, string> = {};
  for (const grp of groups ?? []) {
    for (const o of grp.options ?? []) {
      if (o.color) map[o.value.toLowerCase()] = o.color;
    }
  }
  return map;
}

// Colored status pills for enum-family columns that carry structured `groups` (Slice 10). Handles a
// scalar (enum/ranked_enum) or an array (multi_enum) cell; an option with no color renders as a
// plain (uncolored) pill so a partly-colored config still reads cleanly.
function PillRenderer(params: ICellRendererParams) {
  const colorMap = (params.colDef?.cellRendererParams?.colorMap ?? {}) as Record<string, string>;
  // §5.9 / P12 — diff render for the MULTI-valued case. A scalar enum has nothing to diff (one value
  // replaces one value), so `multiValueDiff` on it degenerates to "removed the old, added the new",
  // which is the honest reading and also what the existing green-text rule already conveys; keep the
  // scalar on its plain path so a single-select pill does not suddenly show two.
  const entries = Array.isArray(params.value) ? multiValueDiffFor(params) : null;
  if (entries) {
    if (entries.length === 0) return <span className={styles.gridEmpty}>—</span>;
    return (
      <span className={styles.gridChips}>
        {entries.map((d, i) => (
          <span key={i} className={`${styles.pill} `
            + `${PILL_CLASS[colorMap[d.value.toLowerCase()]] ?? styles.pillPlain} `
            + `${DIFF_CLASS[d.state]}`}>{d.value}</span>
        ))}
      </span>
    );
  }
  const dv = displayValueForMode(params.data, params.colDef?.field, params.value, params.context?.valueMode);
  const vals = (Array.isArray(dv) ? dv : [dv])
    .map((x) => (x == null ? '' : String(x))).filter((s) => s !== '');
  if (vals.length === 0) return <span className={styles.gridEmpty}>—</span>;
  return (
    <span className={styles.gridChips}>
      {vals.map((val, i) => (
        <span key={i} className={`${styles.pill} ${PILL_CLASS[colorMap[val.toLowerCase()]] ?? styles.pillPlain}`}>{val}</span>
      ))}
    </span>
  );
}

// The identity `name` column: value + a hover ⋯ that opens the row-actions menu (Edit name / Delete
// record). The menu itself is a single grid-level popover keyed by row id — the renderer just hands
// its anchor + entry id up through grid context (AG cell renderers are otherwise isolated).
export function NameCellRenderer(params: ICellRendererParams) {
  const ctx = params.context as {
    openRowMenu?: (entryId: number, anchorEl: HTMLElement) => void;
    startPinnedAdd?: () => void; addLabel?: string;
    openRecord?: (entryId: number) => void;
    acceptRecord?: (entryId: number, label: string) => void;
  };
  // Slice 13: the always-visible blank "add a record" row is a pinned bottom row — render a muted
  // affordance instead of a value/⋯ menu. Opportunity lists edit the name inline (click → text
  // editor); person/org lists open the directory lookup.
  if (params.node.rowPinned === 'bottom') {
    return (
      <button className={styles.addRowCell} onClick={() => ctx.startPinnedAdd?.()}>
        + {ctx.addLabel ?? 'Add a record'}
      </button>
    );
  }
  const id = params.data?.id;
  const text = cellText(params.value);
  // R8: the name cell is the record-panel OPEN affordance, uniform across list kinds — click → open
  // the record. The person/org profile jump that used to live here now lives in the panel's
  // "Open full profile" (the panel subsumes the profile as the primary record surface).
  return (
    // R8/§6.1: double-click-to-edit on the name is dropped so single-click-opens is unambiguous —
    // stop the dblclick from reaching AG's cell edit trigger. Programmatic edit (F2 / the ⋯ "Edit
    // name" → startEditingCell) is unaffected. The pinned add-row is a different render branch above.
    <span className={styles.nameCell} onDoubleClick={(e) => e.stopPropagation()}>
      {id != null && text && ctx.openRecord ? (
        <button className={styles.nameCellLink} onClick={() => ctx.openRecord!(Number(id))}>{text}</button>
      ) : (
        <span className={styles.nameCellText}>{text || <span className={styles.gridEmpty}>—</span>}</span>
      )}
      {/* Item 3a: proposed rows get hover ✓/✕ quick actions on the name cell (not buried in the ⋯).
          ✓ = accept (promote → log deal); ✕ = "other" → open the panel for more options. */}
      {isProposedRecordRow(params.data) && id != null && (
        <span className={styles.rowQuickActions}>
          <button
            className={`${styles.rowQuickBtn} ${styles.rowQuickAccept}`}
            aria-label="Accept record" title="Accept → log deal"
            onClick={(e) => { e.stopPropagation(); ctx.acceptRecord?.(Number(id), text); }}
          >✓</button>
          <button
            className={`${styles.rowQuickBtn} ${styles.rowQuickOther}`}
            aria-label="More options" title="More options (open panel)"
            onClick={(e) => { e.stopPropagation(); ctx.openRecord?.(Number(id)); }}
          >✕</button>
        </span>
      )}
      {id != null && ctx.openRowMenu && (
        <button
          className={styles.rowMenuBtn}
          aria-label="Row actions"
          onClick={(e) => { e.stopPropagation(); ctx.openRowMenu!(Number(id), e.currentTarget); }}
        >⋯</button>
      )}
    </span>
  );
}

// Enhancement B: the derived `current_organization` column links to the org profile using the
// companion `current_organization_id` the backend rides alongside the name string.
export function OrgLinkRenderer(params: ICellRendererParams) {
  const ctx = params.context as { openOrganization?: (id: number) => void };
  const name = cellText(params.value);
  if (!name) return <span className={styles.gridEmpty}>—</span>;
  const id = params.data?.current_organization_id;
  return id != null && ctx.openOrganization ? (
    <button className={styles.gridRefLink} onClick={() => ctx.openOrganization!(Number(id))}>{name}</button>
  ) : <span>{name}</span>;
}

// E4: the derived `latest_note` column shows the latest note's preview and links to the note overlay
// via the companion `latest_note_id` the backend rides alongside the preview string (mirrors OrgLink).
export function NoteLinkRenderer(params: ICellRendererParams) {
  const ctx = params.context as { openNote?: (id: number) => void };
  const preview = cellText(params.value);
  if (!preview) return <span className={styles.gridEmpty}>—</span>;
  const id = params.data?.latest_note_id;
  return id != null && ctx.openNote ? (
    <button className={styles.gridRefLink} onClick={() => ctx.openNote!(Number(id))}>{preview}</button>
  ) : <span>{preview}</span>;
}

/**
 * W5 §10.3: the Open-AI-update-count cell — a number that opens the record.
 *
 * NAVIGATION ONLY (§11.3: "Clicking Open AI updates navigates/scrolls to reviewable fields; it must
 * not accept anything"). A count of 0 is an em dash and NOT a link: there is nothing to open, and a
 * clickable zero would teach people that the number is a button rather than a fact.
 *
 * Scrolling to the panel's pending section specifically is W6 item 5 (the panel owns that anchor);
 * this opens the record, which is the navigation the grid can honestly perform today.
 */
export function PendingCountRenderer(params: ICellRendererParams) {
  const ctx = params.context as { openRecord?: (entryId: number) => void };
  const n = Number(params.value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return <span className={styles.gridEmpty}>{SYSTEM_NULL_DISPLAY}</span>;
  const entryId = params.data?.id;
  if (entryId == null || !ctx.openRecord) return <span>{n}</span>;
  return (
    <button className={styles.gridRefLink} title={`Open the record to review ${n} proposed update${n === 1 ? '' : 's'}`}
            onClick={() => ctx.openRecord!(Number(entryId))}>{n}</button>
  );
}

// RefCellEditor + _normalizeRefs live in ./CrmRefCellEditor (extracted in D2b so the record panel can
// re-host the editor without importing CrmListGrid → a circular import). Re-exported here so callers /
// tests that import { RefCellEditor } from './CrmListGrid' keep resolving.
export { RefCellEditor };

// ── custom multi-select "set" filter (Community — the built-in set filter is Enterprise) ──────
function SetFilter({ model, onModelChange, api, colDef, enumLabels }:
CustomFilterProps & { enumLabels?: Record<string, string> | null }) {
  const field = colDef.field as string;
  const [q, setQ] = useState('');
  const selectedSet = useMemo(() => new Set(Array.isArray(model) ? model : []), [model]);
  // W5 §10.3/§12.4: the label layer reaches the FILTER list too, and this is the half that makes
  // "raw values are byte-stable" survive contact with a user. The option list and the saved
  // `filterModel` stay RAW — `toggle` and `doesFilterPass` never see a label — while the checkbox and
  // the search box read the display vocabulary. So a user ticks "Proposed" and the view saves
  // "AI proposed", which is exactly what the two prod saved views already contain, and an OLD saved
  // filter keeps matching without a migration (§12.4's alias, unnecessary by construction).
  //
  // Arrives through `filterParams`, AG's own mechanism for handing a custom filter its column's
  // configuration, rather than as an off-schema key on `colDef`.
  const labelOf = useCallback(
    (raw: string) => (enumLabels ? (enumLabels[raw] ?? raw) : raw), [enumLabels]);

  // distinct values, computed from the loaded rows via the (stable) grid api — filters mount
  // lazily on first open, by which point rowData is present.
  const options = useMemo(() => {
    const set = new Set<string>();
    let hasBlank = false;
    api.forEachNode((node) => {
      const nonEmpty = cellValues(node.data?.[field]).filter((s) => s !== '');
      if (nonEmpty.length === 0) hasBlank = true;
      nonEmpty.forEach((s) => set.add(s));
    });
    const arr = [...set].sort((a, b) => a.localeCompare(b));
    if (hasBlank) arr.unshift(''); // '' → the selectable "(blank)" option, first
    return arr;
  }, [api, field]);

  const doesFilterPass = useCallback(
    (params: { data: Record<string, unknown> }) => {
      if (!Array.isArray(model) || model.length === 0) return true;
      const sel = new Set(model);
      const nonEmpty = cellValues(params.data?.[field]).filter((s) => s !== '');
      // an empty/blank cell matches only when the "(blank)" option ('') is selected
      if (nonEmpty.length === 0) return sel.has('');
      return nonEmpty.some((s) => sel.has(s));
    },
    [model, field],
  );
  useGridFilter({ doesFilterPass });

  const toggle = (val: string) => {
    const next = new Set(selectedSet);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onModelChange(next.size ? [...next] : null);
  };
  // search matches the DISPLAYED text, so typing "prop" finds the row the user can see. The values
  // it filters are still the raw ones.
  const shown = options.filter((o) => labelOf(o).toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className={styles.setFilter}>
      <input className={styles.setFilterSearch} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className={styles.setFilterActions}>
        <button onClick={() => onModelChange(shown.length ? shown : null)}>All</button>
        <button onClick={() => onModelChange(null)}>None</button>
      </div>
      <div className={styles.setFilterList}>
        {shown.length === 0 && <div className={styles.gridEmpty}>No values</div>}
        {shown.map((o) => (
          <label key={o} className={styles.setFilterRow}>
            <input type="checkbox" checked={selectedSet.has(o)} onChange={() => toggle(o)} />
            <span>{labelOf(o) || '(blank)'}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── column defs from the list's field schema ──────────────────────────────────────────────────
/**
 * §6.2: does a cell hold more than it shows? A single-line cell overflows HORIZONTALLY on the cell
 * itself. A wrapped-and-clamped long-text cell (`.gridClampText`) overflows VERTICALLY, and on the
 * inner value box — the clamp lives there, so the cell's own scrollHeight never exceeds its height.
 * Either dimension on the cell or its AG wrappers counts.
 */
export function cellContentOverflows(cellEl: HTMLElement): boolean {
  const els: HTMLElement[] = [cellEl, ...Array.from(
    cellEl.querySelectorAll<HTMLElement>('.ag-cell-wrapper, .ag-cell-value'))];
  return els.some((el) => el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
}

export function buildColumnDefs(columns: CrmGridColumn[], editable: boolean,
                         options: Record<string, string[]>, refEditable: boolean,
                         /** PR3 item 7: {field_key → eligible people} for roster-flagged ref columns */
                         refOptions: Record<string, CrmRosterOption[]> = {},
                         /** plan 1b: `stage_mapping` ladder membership; null ⇒ no Stage contingency */
                         ladders: CrmStageLadders | null = null): ColDef[] {
  return columns.map((c) => {
    const base: ColDef = {
      field: c.field_key,
      headerName: c.label,
      resizable: true,
      sortable: true,
      // Slice 6: the custom header (explainer + ⋮ actions menu). Per-column static info rides in
      // headerComponentParams; the action callbacks come live off the grid context. headerComponent
      // itself is set once on defaultColDef.
      headerComponentParams: {
        origin: c.origin ?? null, fieldType: c.field_type, fieldKey: c.field_key,
        // W5 (§9.2 / §7.4): the header decides rename/delete from `systemFamily` — catalog
        // membership — never from `origin`, and shows the definition in its explainer popover.
        systemFamily: c.system_family ?? null, definition: c.definition ?? null,
      },
    };
    // overlay-pending (blue) + AI-proposal (green) marks apply to EVERY column (a proposal can
    // land on a field the grid doesn't inline-edit); tooltips explain both states.
    const fk0 = c.field_key;
    // R4 (§6.1): a proposed cell is read-only in AI mode (accept/dismiss via the review bar rather
    // than typing over a proposal); editable otherwise. Assigned only where the column is editable.
    const editableCb = (p: { context?: { valueMode?: ValueMode }; data?: Record<string, unknown> }): boolean =>
      !(p.context?.valueMode === 'ai' && !!cellProposal(p.data, fk0));
    base.cellClassRules = {
      [styles.gridPendingCell]: (p) =>
        (p.data?._overlay as Record<string, { status?: string }> | undefined)?.[fk0]?.status === 'pending',
      [styles.gridProposalCell]: (p) =>
        !!(p.data?._proposals as Record<string, unknown> | undefined)?.[fk0],
      // R4 (§6.1): in AI mode a proposed cell prints the proposed value → bold-green text (the green
      // background above marks "review here" in BOTH modes; this marks "this IS the AI value").
      [styles.gridProposedValue]: (p) => showsProposedValue(p),
    };
    base.tooltipValueGetter = (p) => {
      const mark = (p.data?._overlay as Record<string, { status?: string; set_by?: string | null; push_error?: string | null }> | undefined)?.[fk0];
      if (mark?.status === 'pending') {
        // "pending" is now an UNSENT edit, not a queued one: the Pillar-D funeral (2026-08-27) deleted
        // the drain, so nothing will ever push this cell. It is resolved by the next import's
        // `reconcile_overlay` (fulfilled or superseded). Saying "pending Affinity sync" would promise
        // an event that can no longer happen and send the operator hunting for a Sync control that
        // went with the drain. `push_error` can only be a pre-funeral value now, so it reads as one.
        const t = `Edited by ${mark.set_by ?? 'unknown'} — not synced to Affinity (the sync was retired)`;
        return mark.push_error ? `${t} · an earlier sync had failed: ${mark.push_error}` : t;
      }
      const prop = (p.data?._proposals as Record<string, { value?: unknown }> | undefined)?.[fk0];
      if (prop) return `AI proposes: ${cellText(prop.value)} — click to review`;
      return null;
    };

    // The identity `name` column pins left so it stays visible while scrolling wide grids. On
    // person/org lists it's graph-derived (origin='derived') → read-only (the directory record owns
    // the name, not the list cell); on opportunity lists `name` is a real editable field_def.
    if (c.field_key === 'name') {
      base.pinned = 'left';
      base.lockPinned = true;
      base.minWidth = 180;
      base.cellRenderer = NameCellRenderer; // value + the row-actions ⋯ menu (+ profile link)
    }
    // Enhancement B: the derived Organization column links to the org profile (companion id on the row)
    if (c.field_key === 'current_organization' && c.origin === 'derived') {
      base.cellRenderer = OrgLinkRenderer;
    }
    // E4: the Latest Note column links to the note overlay (companion latest_note_id on the row).
    // ⚠ `isComputedColumn`, not `origin === 'derived'`: `latest_note` is a CATALOG key, so W5's §9.2
    // flip moved its origin to 'system' — the old guard would have silently dropped the note link on
    // every person and organization list. It stays a GUARD rather than a bare key test so an
    // operator's own stored `latest_note` column does not get a link to a note it has no id for.
    if (c.field_key === 'latest_note' && isComputedColumn(c)) {
      base.cellRenderer = NoteLinkRenderer;
    }
    // W5: computed = read_only (every system field) ∪ graph-derived identity ∪ view-only. NEVER
    // `origin === 'system'`, which is also the stored, EDITABLE opportunity `name` field def.
    const isDerived = isComputedColumn(c); // computed, never inline-editable

    // Slice 10: a value→color map when the enum-family column carries structured groups → colored pills
    const colorMap = c.groups && c.groups.length ? colorMapFromGroups(c.groups) : null;
    const opts = options[c.field_key];
    const editableType = EDITABLE_TYPES.has(c.field_type)
      || (c.field_type === 'ranked_enum' && !!opts?.length); // stage: only with a vocabulary
    if (editable && editableType && !isDerived) {
      base.editable = editableCb;
      if ((c.field_type === 'enum' || c.field_type === 'ranked_enum') && opts?.length) {
        // Plan 1b: the Stage column's visible ladder is a per-ROW question (the record's `deal_type`
        // picks it), so a SELECTOR rather than a column-level editor. The editor CHOICE has to move
        // in here too: with both ladders stored the column-level `> 20` would put every buy-side row
        // on the searchable checklist to pick from a 13-option ladder. Every other enum column
        // resolves exactly as before — `stageLadderOptions` hands `opts` straight back.
        base.cellEditorSelector = (p: { value: unknown; data?: Record<string, unknown> }) => {
          const rowOpts = stageLadderOptions(
            fk0, opts, p.value, dealTypeOf(p.data, ladders), ladders) ?? opts;
          if (rowOpts.length > 20) {
            // long picklist → searchable single-select checklist (E6.4) instead of a giant <select>
            return { component: CrmChecklistEditor, popup: true,
                     params: { options: rowOpts, single: true } };
          }
          // include the CURRENT value (and '' for blank cells) so the editor initializes on it —
          // otherwise agSelectCellEditor defaults to the FIRST option and a double-click + blur
          // silently commits a value the operator never chose (adv-review).
          const cur = p.value == null || p.value === '' ? '' : String(p.value);
          return { component: 'agSelectCellEditor',
                   params: { values: cur && rowOpts.includes(cur) ? rowOpts : [cur, ...rowOpts] } };
        };
      } else {
        base.cellEditor = CELL_EDITORS[c.field_type];
      }
      if (c.field_type === 'long_text') base.cellEditorPopup = true;
      base.cellClass = styles.gridEditable;
    }
    if (c.field_type === 'long_text') {
      // Long text (Opportunity Description, Next Steps, notes…) WRAPS inside the cell but never grows
      // the row: `wrapText` without `autoHeight`, so the row height stays whatever the other cells
      // (the entity-list columns above all) already set, and the paragraph fills that space instead
      // of a single clipped line. `.gridClampText` top-aligns it and clips to WHOLE lines (the
      // vertical centring in `.ag-cell-wrap-text` would cut the first and last line in half). The
      // rest of the value is one click away in the §6.2 inspector, which also fires on vertical
      // overflow (cellContentOverflows).
      base.wrapText = true;
      base.cellClass = base.cellClass ? [base.cellClass as string, styles.gridClampText] : styles.gridClampText;
    }
    if (REF_TYPES.has(c.field_type)) {
      const kind = c.field_type.startsWith('person') ? 'person' : 'organization';
      base.cellRenderer = RefsRenderer;
      base.cellRendererParams = { kind };
      base.comparator = (a, b) => cellText(a).localeCompare(cellText(b));
      base.filter = SetFilter;
      // §5.7(e): entity-LIST columns are allowed to wrap — a deal team or an advisor list is several
      // names and a single clipped line hid most of them. `autoHeight` grows the row to fit; the
      // vertical centring of the wrapped text is `.ag-cell-wrap-text` in crm.module.css. Single-ref
      // columns are excluded: one name never needs a second line, and per-row heights are not free.
      if (c.field_type.endsWith('_multi')) {
        base.wrapText = true;
        base.autoHeight = true;
      }
      // L2: entity-ref columns are editable ONLY on native lists (refEditable) — the backend refuses
      // ref edits on Affinity-backed lists (a ref write-back is a future W1 push). The chip popup
      // commits [{local_id,name}] / a single dict / null; onCellEdit PATCHes via the normal path.
      if (refEditable && !isDerived) {
        const single = c.field_type === 'person' || c.field_type === 'organization';
        // PR3 item 7: a ROSTER-ONLY ref column (`ref_options_source:'roster'` — the flag, never the
        // field key) gets the eligible-people checklist instead of the open directory typeahead, once
        // its picklist has arrived. No people yet → the typeahead, whose non-roster picks the server
        // still refuses; an empty checklist would be a dead end.
        const people = c.ref_options_source === ROSTER_REF_SOURCE ? refOptions[c.field_key] : undefined;
        base.editable = editableCb;
        base.cellEditor = people?.length ? CrmRosterPicker : RefCellEditor;
        base.cellEditorPopup = true;
        base.cellEditorParams = people?.length ? { people, single } : { kind, single };
        base.cellClass = styles.gridEditable;
      }
    } else if (c.field_type === 'multi_enum') {
      // colored pills when the column has structured groups (Slice 10); else plain muted chips
      base.cellRenderer = colorMap ? PillRenderer : ChipsRenderer;
      if (colorMap) base.cellRendererParams = { colorMap };
      base.comparator = (a, b) => cellText(a).localeCompare(cellText(b));
      base.filter = SetFilter;
      // multi-select editing (E6.4): searchable checklist popup committing string[]. C3 (§6.3):
      // enabled on BOTH native AND overlay lists — the backend accepts overlay multi_enum edits
      // (they route through _set_overlay_value like any scalar); only entity refs stay native-only.
      if (editable && !isDerived && opts?.length) {
        base.editable = editableCb;
        base.cellEditor = CrmChecklistEditor;
        base.cellEditorPopup = true;
        base.cellEditorParams = { options: opts, single: false };
        base.cellClass = styles.gridEditable;
      }
    } else if (c.field_type === 'enum' || c.field_type === 'ranked_enum') {
      base.filter = SetFilter;
      // Slice 10: a grouped/colored enum (e.g. Status) renders as a single colored pill (the editable
      // select still opens on double-click — renderer + editor coexist).
      if (colorMap) { base.cellRenderer = PillRenderer; base.cellRendererParams = { colorMap }; }
      // W5 §10.3 / §20 Q9: the LABEL-layer vocabulary migration — 'AI proposed' renders as
      // 'Proposed'. The raw value is untouched, so the saved set-filter model, the sort order and the
      // API response stay byte-identical to W2 — which is what §12.4 requires and what makes its
      // saved-filter migration a no-op. The map is server-owned (`enum_labels`), so a later
      // vocabulary change is one backend constant and no frontend release.
      if (c.enum_labels) {
        base.valueFormatter = (p) => enumDisplay(
          c, displayValueForMode(p.data, fk0, p.value, p.context?.valueMode));
        base.filterParams = { enumLabels: c.enum_labels };
      }
    } else if (NUMERIC_TYPES.has(c.field_type)) {
      base.filter = 'agNumberColumnFilter';
      base.type = 'numericColumn';
      if (c.field_type === 'currency') base.valueFormatter = (p) => {
        const dv = displayValueForMode(p.data, fk0, p.value, p.context?.valueMode); // R4: proposed $ in AI mode
        return dv == null || dv === '' ? '' : `$${dv}`;
      };
      // W5 §10.3: "Open AI update count is numeric and may open the panel's pending-review section."
      // NAVIGATION ONLY — §11.3 is explicit that clicking must not accept anything.
      if (c.field_key === OPEN_AI_UPDATE_COUNT_FIELD) {
        base.cellRenderer = PendingCountRenderer;
      }
    } else {
      base.filter = 'agTextColumnFilter'; // text / long_text / date (ISO string)
      // L3b (Slice 14) + W5 §10.3: COMPUTED date columns (the graph-derived interaction dates and
      // every system-field date) render as a relative day ("3d ago" / "in 2d"); a null renders an EM
      // DASH — never 0, never "Never" (§12/S12: null means "no entitled, attributable value is
      // known"). A real editable `date` field stays a plain ISO cell so the text editor round-trips.
      if (isDerived && c.field_type === 'date') {
        base.valueFormatter = (p) => (
          p.value == null || p.value === ''
            ? SYSTEM_NULL_DISPLAY
            : fmtRelativeDay(p.value as string | null));
        // §7.4's definition/freshness tooltip. On a SYSTEM column it carries the exact timestamp, the
        // field's definition, what a BLANK means (the §7.3 coverage state — "not calculated yet" vs
        // "no such date is known", the distinction that stops the grid implying "never contacted"),
        // and when the value was computed. On a plain graph-derived date it degrades to exactly what
        // it showed before: the exact date.
        base.tooltipValueGetter = (p) => (
          c.system_family
            ? systemCellTooltip(c, {
              // ⚠ `|| null` was DEAD: fmtShortDate(null) returns the em dash, which is truthy, so
              // the tooltip led with a bare "—" immediately before the sentence explaining why the
              // cell is blank (review finding 11). Gate on the VALUE, not on the formatter's output.
              exact: p.value == null || p.value === ''
                ? null : fmtShortDate(p.value as string | null),
              ...systemCoverageFor(p.data, c, fmtShortDate),
              isBlank: p.value == null || p.value === '',
            })
            : fmtShortDate(p.value as string | null));
      }
    }
    return base;
  });
}

// Force the identity `name` column pinned-left in a saved-view columnState before applyColumnState
// re-applies it. A view saved BEFORE the pinned-identity fix serialized {colId:'name', pinned:null};
// applyColumnState honors that null and unpins the (lockPinned) column with no Community UI to re-pin
// it. Re-stamping pinned:'left' here keeps the identity column pinned across view loads (adv-review).
/** The columns the grid PINS and re-pins on every applyColumnState — so nothing may reorder them.
 *  Handed to the Columns panel, which renders them undraggable rather than offering a drag the grid
 *  would silently undo. */
export const PINNED_COLUMN_KEYS = new Set(['name']);

export function pinNameColumnState(state: unknown): unknown {
  if (!Array.isArray(state)) return state;
  return state.map((s) =>
    s && typeof s === 'object' && (s as { colId?: string }).colId === 'name'
      ? { ...(s as object), pinned: 'left' }
      : s);
}

// D8: is this row a whole-record PROPOSAL? Drives the full-row-green treatment (rowClassRules) AND the
// Accept/Dismiss row-menu gate, so the tint and the actionable affordance can never disagree. Ignores
// the pinned add-row (no marker).
//
// The implementation moved to `crmAiProposedRow` so the record panel can share it — see there. Kept
// re-exported here because this is the name every grid call site (and its tests) already import.
export { isProposedRecordRow };

// D8 + §5.7(a)/(b) + F.6: the row-level lifecycle treatment (candidate green, active-record fill +
// accent). It moved to `crmActiveRow` when F.6 made it close over the OPEN entry id — it can no
// longer be a module constant, and the redraw it now needs (AG evaluates row class rules at row
// render time) belongs next to it. Re-exported because this is the name the grid's tests import.
export { buildRowClassRules };

// D8: the row-actions menu contents (rendered inside the grid-level CrmPopover). Extracted as a pure
// presentational unit so the menu's per-row-kind composition is directly testable (the full AG-Grid
// doesn't render rows under jsdom). A PROPOSED record's unit of decision is the WHOLE row — it offers
// Accept (promote → log deal) / Dismiss (suppress), and HIDES per-field affordances (Edit name /
// Delete) which the backend rejects on a proposed entry (audit #4). "Open record" is always available.
export function RowActionsMenu(props: {
  isNative: boolean;
  isOpportunity: boolean; // subject_kind === null (the name is an editable field, not a directory link)
  isProposed: boolean;
  onOpen: () => void;
  onEditName: () => void;
  onDelete: () => void;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const { isNative, isOpportunity, isProposed, onOpen, onEditName, onDelete, onAccept, onDismiss } = props;
  return (
    <>
      {/* §6.1: explicit "Open record" — the discoverable alternate to the name-cell click; works
          across all list kinds AND for blank-name rows (whose name cell renders no button). */}
      <button role="menuitem" className={styles.popoverItem} onClick={onOpen}>Open record</button>
      {isProposed && (
        // D8: whole-record decision — Accept commits the AI's proposed row as a real deal on this
        // list. Consequential, so the grid gates it behind a confirm.
        <>
          <button role="menuitem" className={`${styles.popoverItem} ${styles.popoverAccept}`} onClick={onAccept}>
            Accept record → log deal
          </button>
          <button role="menuitem" className={styles.popoverItem} onClick={onDismiss}>Dismiss record</button>
        </>
      )}
      {/* per-field affordances are for COMMITTED rows only — a proposed row is decided as a whole */}
      {isOpportunity && !isProposed && (
        <button role="menuitem" className={styles.popoverItem} onClick={onEditName}>Edit name</button>
      )}
      {/* Delete only on native lists — the Affinity-backed IOT owns its rows (backend 400s otherwise) */}
      {isNative && !isProposed && (
        <button role="menuitem" className={`${styles.popoverItem} ${styles.popoverDanger}`} onClick={onDelete}>
          Delete record
        </button>
      )}
    </>
  );
}

export function CrmListGrid({ listId, onBack, openPerson, openOrg, openMsg, openNote }: Props) {
  const grid = useCrmGrid(listId, { start_row: 0, end_row: FETCH_CAP });
  const apiRef = useRef<GridApi | null>(null);
  // apiRef is null until onGridReady; gate the view/column controls on this so they can never run
  // against a null api (which would e.g. persist an EMPTY config over a saved view — review finding).
  const [gridReady, setGridReady] = useState(false);
  const [search, setSearch] = useState('');
  const [panel, setPanel] = useState<'columns' | 'views' | 'addcol' | 'addmember' | 'editfield' | 'filter' | 'sort' | null>(null);
  // Slice 9 / R3: the pure-FE filter builder tree + search scope + AI-Updates filter. All are view
  // state (persisted in the saved-view config) and drive AG's external filter. proposalsFilter
  // replaces the old proposals-only boolean (spec §5.4).
  const [filterTree, setFilterTree] = useState<FilterGroup>({ kind: 'group', logic: 'and', children: [] });
  const [searchScope, setSearchScope] = useState<'anywhere' | 'names'>('anywhere');
  const [proposalsFilter, setProposalsFilter] = useState<ProposalsFilter>('off');
  // remember the last non-'off' scope so the AI-Updates primary button toggles back to it
  const lastProposalsScopeRef = useRef<Exclude<ProposalsFilter, 'off'>>('all');
  // §1.2 compact search-scope menu + §1.4 Save caret menu — anchor the CrmPopover to the clicked
  // element via state (set from the click handler's currentTarget), never a ref read during render.
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState<HTMLElement | null>(null);
  const [saveMenuAnchor, setSaveMenuAnchor] = useState<HTMLElement | null>(null);
  // R4 (§6.1): the [Current|AI] mode changes displayed values and proposed-row visibility, so it is
  // saved with the view (including shared/default views) like every other result-shaping setting.
  const [valueMode, setValueMode] = useState<ValueMode>('current');
  // The filter builder and AG's per-column filters intentionally compose with AND. Track the latter
  // separately so the toolbar and filter dock can make that composition visible and clearable.
  const [columnFilterCount, setColumnFilterCount] = useState(0);
  // Slice 6: the field under edit in the dock (rename); the pending column-insert anchor + the
  // post-create reorder to place the new column adjacent to that anchor.
  const [editField, setEditField] = useState<EditField | null>(null);
  const [convertError, setConvertError] = useState<string | null>(null); // Slice 7: retype error, shown in the panel
  const [insertTarget, setInsertTarget] = useState<{ fieldKey: string; side: 'left' | 'right' } | null>(null);
  const [pendingReorder, setPendingReorder] = useState<{ newKey: string; anchorKey: string; side: 'left' | 'right' } | null>(null);
  const [loadedView, setLoadedView] = useState<CrmView | null>(null);
  // Views v2 (E3): dirty = the grid diverges from the loaded view; baselineRef holds the loaded
  // view's normalized config key; gridRev bumps on every AG layout event to re-derive dirty.
  const [dirty, setDirty] = useState(false);
  const [gridRev, setGridRev] = useState(0);
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [sharing, setSharing] = useState(false);   // list-sharing v1: the Share dialog (transient)
  const baselineRef = useRef<string | null>(null);
  const autoAppliedRef = useRef(false); // apply the default view once per mount (grid is keyed by list)
  // The last-known-good column state, re-applied after a colDef REBUILD wipes column widths.
  // `defaultColDef.flex:1` is a colDef property, so every setColumnDefs (fired when `fieldOptions`
  // resolves — a separate async query — or on any add/remove/refetch) re-asserts flex:1 on all
  // columns and a flex column ignores its fixed width, silently resetting the widths the saved view
  // (or a manual resize) had applied. maintainColumnOrder saves order and AG keeps sort/visibility
  // runtime state, so ONLY width regresses. We capture the live state on every layout change + after
  // applyView, and re-apply width/flex after each rebuild (below) so widths survive. (Verified in the
  // running app — this is an AG timing/flex interaction jsdom can't exercise.)
  const liveColStateRef = useRef<ColumnState[] | null>(null);
  const colDefsInitRef = useRef(true); // skip the initial colDefs (mount) — tryApplyDefault owns that
  const [newCol, setNewCol] = useState<{ label: string; type: string; options: string }>({ label: '', type: 'text', options: '' });
  // M2: inline editor for an existing enum-family column's option picklist ({field_key, textarea text}).
  const [optionsEdit, setOptionsEdit] = useState<{ fieldKey: string; text: string } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  // The Columns panel is the ONLY place columns are hidden (Community has no columns tool panel),
  // so `hidden` is the single source of truth — no reading the grid ref during render.
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // The grid's LIVE column order (field_keys), mirrored into state so the Columns panel renders its
  // rows in the order the grid shows them (owner request 2026-08-31). Synced from an effect keyed on
  // `gridRev` — which already bumps on every column move/visibility/pin change — rather than read off
  // `apiRef` during render, for the same reason `hidden` is state.
  const [colOrder, setColOrder] = useState<string[]>([]);
  // the row whose ⋯ actions menu is open (anchored at the clicked ⋯ button). One grid-level popover.
  const [rowMenu, setRowMenu] = useState<{ entryId: number; anchorEl: HTMLElement } | null>(null);
  const deleteEntry = useDeleteEntry(listId);
  // locks the shell height so the grid scrolls internally (E1); destructured so the ref + the height
  // (plain state) are distinct locals (the lint ref-tracker flags property access off a ref-bearing object).
  // called BEFORE useViewportFill so its layout effect (which applies the #root min-height:0 class)
  // runs before the fill measurement in the same commit — otherwise the first paint squishes.
  useChromeless(); // full-viewport list grid: hide the app's big header + footer while open
  const { ref: shellRef, height: shellHeight } = useViewportFill();
  const agWrapRef = useRef<HTMLDivElement>(null); // measured for the autosize width cap (E6.7)
  // Item 2b: the record panel's left edge is set from the summed left-pinned column width via the
  // --crm-pinned-w CSS var on this element (the panel's positioning context) — so the panel edge and
  // the name-column border move together. Recomputed on column resize/pin/move/visibility + grid size.
  const gridBodyRef = useRef<HTMLDivElement>(null);
  const updatePinnedWidth = useCallback(() => {
    const api = apiRef.current;
    const el = gridBodyRef.current;
    if (!api || !el) return;
    const cols = api.getDisplayedLeftColumns?.() ?? [];
    const w = cols.reduce((s, c) => s + (c.getActualWidth?.() ?? 0), 0);
    // clamp: never narrower than the name minWidth, never wider than 40vw (a fat rail can't crush the
    // panel — the clamp wins over exact alignment above 40vw, §7.1).
    const clamped = Math.max(180, Math.min(w || 240, Math.round(window.innerWidth * 0.4)));
    el.style.setProperty('--crm-pinned-w', `${clamped}px`);
  }, []);

  const views = useCrmListViews(listId);
  // Warm the per-list record layout at grid-load (shared list-keyed query) so the record panel opens
  // in the final field order with no reorder "jitter" — the container reads the same cached query.
  useCrmRecordLayout(listId);
  const createView = useCreateView(listId);
  const updateView = useUpdateView(listId);
  const deleteView = useDeleteView(listId);
  const setTeamDefaultView = useSetTeamDefaultView(listId);
  // Admin gate for "Set as team default", read off the VIEWS PAYLOAD — the same capability resolution
  // that gates the route. Deliberately NOT `isAdmin(useAuthUser().email)`: that consults the hardcoded
  // ADMIN_EMAILS mirror in auth/admins.ts, whereas require_admin ALSO honors `team_roster.is_admin` —
  // so a roster-flagged admin passed the route and never saw the button. The server stays the
  // authority; this only keeps a doomed request off the wire.
  const canSetTeamDefault = views.data?.is_admin === true;
  const addField = useAddField(listId);
  const updateField = useUpdateField(listId);
  const deleteField = useDeleteField(listId);
  const convertField = useConvertFieldType(listId);
  const setFieldConfig = useSetFieldConfig(listId);
  const renameOption = useRenameOption(listId);
  const deleteOption = useDeleteOption(listId);
  const setListDerived = useSetListDerived(listId);
  const setFieldOptions = useSetFieldOptions(listId);
  const addEntry = useAddEntry(listId);
  const addMember = useAddMember(listId);
  const setFieldValue = useSetFieldValue(listId);
  // L1 member-add typeahead (person/org lists): the raw input drives the field; a 250ms-debounced
  // copy drives useMemberLookup so we don't fire a directory query on every keystroke.
  const [memberInput, setMemberInput] = useState('');
  const [memberQ, setMemberQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setMemberQ(memberInput), 250);
    return () => clearTimeout(t);
  }, [memberInput]);
  const memberLookup = useMemberLookup(listId, memberQ);
  const listOptions = useCrmListOptions(listId);
  const acceptProposal = useAcceptProposal(listId);
  const dismissProposal = useDismissProposal(listId);
  // D8: whole-record proposal actions (Accept = promote; Dismiss = suppress). dismissRecordTarget
  // drives the reason modal; Accept is gated behind a window.confirm (consistent with delete).
  const acceptRecord = useAcceptRecord(listId);
  const dismissRecord = useDismissRecord(listId);
  const [dismissRecordTarget, setDismissRecordTarget] = useState<{ entryId: number; label: string } | null>(null);
  // the proposal under review (a green cell was clicked): accept/dismiss from the review bar.
  // proposalId is echoed on accept/dismiss so a newer aggregator run → 409 stale (never acts on a
  // value the operator never saw).
  const [review, setReview] = useState<{
    entryId: number; fieldKey: string; label: string; value: unknown; current: unknown;
    proposalId: number;
  } | null>(null);
  // R4 (§6.2): the cell-inspector readout — clicking a NON-proposal cell whose content overflows
  // shows its full value in the bar (read-only). Distinct from `review` (proposal accept/dismiss).
  const [inspect, setInspect] = useState<{ entryId: number; fieldKey: string; label: string } | null>(null);
  const barRef = useRef<HTMLDivElement>(null); // the bar wrapper — click-away ignores clicks inside it

  // BOTH list kinds are editable (M3.2): native = direct local writes; Affinity-backed = the
  // overlay (edits render as pending until Affinity syncs; push lands in M3.3). Structure edits
  // (+Column/+Row) stay native-only — the IOT's schema/rows come from Affinity.
  const isNative = grid.data?.list.list_kind === 'native';
  const editMode = grid.data?.list.edit_mode ?? (isNative ? 'direct' : 'overlay');
  // L1: subject-bearing lists (person/organization) add MEMBERS via the directory lookup, not a
  // blank row. null = opportunity list (the "+ Row" path). Only meaningful on native lists.
  const subjectKind = (grid.data?.list.entry_subject_kind ?? null) as 'person' | 'organization' | null;
  // (the payload still carries `sync: {pending, failed}` — nothing renders it since the Pillar-D
  //  funeral removed the sync dot; the counts are the un-drained-overlay census, not an action)
  const columns = useMemo(() => grid.data?.columns ?? [], [grid.data]);
  const isDealPipeline = grid.data?.list.list_kind === 'deal_pipeline';
  const viewColumns = useMemo(
    () => withAiProposedRowColumn(columns, isDealPipeline), [columns, isDealPipeline]);
  const rows = useMemo(
    () => withAiProposedRowValues(grid.data?.rows ?? [], isDealPipeline), [grid.data?.rows, isDealPipeline]);
  const fieldOptions = useMemo(() => listOptions.data?.options ?? {}, [listOptions.data]);
  const viewFieldOptions = useMemo(() => isDealPipeline ? {
    ...fieldOptions,
    [AI_PROPOSED_ROW_FIELD]: [AI_PROPOSED_ROW_VALUE, CURRENT_ROW_VALUE],
  } : fieldOptions, [fieldOptions, isDealPipeline]);
  const availableDerived = useMemo(() => grid.data?.available_derived ?? [], [grid.data]);
  // W5 (§9.3): the catalog as it applies to THIS list — the Customize View System-fields groups read
  // this, not `available_derived`. Kept beside it rather than replacing it because the two answer
  // different questions: this one is the catalog's state (availability, enablement, definition) and
  // the alias is still what the owner's legacy `/derived` enable path is scoped to.
  const availableSystemFields = useMemo(
    () => grid.data?.available_system_fields ?? [], [grid.data]);
  // PR3 item 7: the roster picklists for `ref_options_source:'roster'` columns, from the SAME options
  // query as the enum picklists (one cache, one invalidation).
  const refFieldOptions = useMemo(() => listOptions.data?.ref_options ?? {}, [listOptions.data]);
  // plan 1b: `stage_mapping` ladder membership, from the same options query. Null on any list the
  // Stage contingency does not apply to, which is what leaves every other list's editors unchanged.
  const stageLadders = listOptions.data?.stage_ladders ?? null;
  const colDefs = useMemo(
    () => buildColumnDefs(viewColumns, !!grid.data, viewFieldOptions, editMode === 'direct',
                          refFieldOptions, stageLadders),
    [viewColumns, grid.data, viewFieldOptions, editMode, refFieldOptions, stageLadders]);
  const defaultColDef = useMemo<ColDef>(() => ({
    minWidth: 110, flex: 1, filter: true, floatingFilter: false,
    menuTabs: ['filterMenuTab'], // the custom header owns the actions menu; keep the filter tab for showColumnFilter
    headerComponent: CrmColumnHeader, // Slice 6: label explainer + ⋮ actions (sort/filter/pin/edit/delete)
    // quick-filter matches by our display string — else person/org ref OBJECTS stringify to
    // "[object Object]" and the top search box silently never matches them (review finding). Note it
    // reads the RAW value (committed), NOT the mode-resolved one — search stays on committed truth.
    getQuickFilterText: (p) => cellText(p.value),
    // R4 (§6.1): plain (non-custom-rendered) cells show the proposed value in AI mode. Columns with
    // a cellRenderer (refs/chips/pills/name) resolve this inside the renderer; columns with their own
    // valueFormatter (currency / derived-date) override this default. valueGetter is untouched, so
    // sort/filter/quick-filter/edit still operate on committed values.
    valueFormatter: (p) => cellText(displayValueForMode(p.data, p.colDef?.field, p.value, p.context?.valueMode)),
  }), []);

  // Returns null when the grid api isn't ready — callers MUST skip persisting, else an empty {}
  // config would overwrite (wipe) the saved view's columnState/filterModel (review finding).
  const captureConfig = (): Record<string, unknown> | null => {
    const api = apiRef.current;
    if (!api) return null;
    return {
      columnState: api.getColumnState(), filterModel: api.getFilterModel(), quickFilter: search,
      filterTree, searchScope, proposalsFilter, valueMode,
    };
  };
  const applyView = (v: CrmView) => {
    const api = apiRef.current;
    if (!api) return; // gated on gridReady; never half-apply (search-only) against a null api
    const c = (v.config ?? {}) as Record<string, unknown>;
    // A view is a full deterministic snapshot. Reset first so columns added after an older view was
    // saved (including AI proposed row) do not inherit hide/sort/pin state from the previously loaded
    // view merely because that old config has no entry for them.
    api.resetColumnState();
    if (Array.isArray(c.columnState)) {
      api.applyColumnState({ state: pinNameColumnState(c.columnState) as never, applyOrder: true });
    }
    api.setFilterModel((c.filterModel as never) ?? null);
    // sync `hidden` from the applied state so the Columns panel reflects the loaded view
    setHidden(new Set(api.getColumnState().filter((s) => s.hide).map((s) => s.colId)));
    // Slice 9 / R3 view state — restore the filter tree / search scope / AI-Updates filter
    const appliedTree = (c.filterTree as FilterGroup) ?? { kind: 'group', logic: 'and', children: [] };
    const appliedScope = (c.searchScope === 'names' ? 'names' : 'anywhere') as 'anywhere' | 'names';
    const coherentAiState = cohereAiViewState(readProposalsFilter(c), readValueMode(c));
    const appliedProposals = coherentAiState.proposalsFilter;
    const appliedValueMode = coherentAiState.valueMode;
    setFilterTree(appliedTree);
    setSearchScope(appliedScope);
    setProposalsFilter(appliedProposals);
    setValueMode(appliedValueMode);
    if (appliedProposals !== 'off') lastProposalsScopeRef.current = appliedProposals;
    setSearch((c.quickFilter as string) ?? '');
    setLoadedView(v);
    // baseline = the state we just applied, read back from the api (so columns that exist NOW but
    // weren't in the saved config — e.g. derived columns added since — are part of the baseline, not
    // a phantom diff). Set synchronously so the dirty effect that follows compares equal → not dirty.
    baselineRef.current = viewConfigKey({
      columnState: api.getColumnState(), filterModel: api.getFilterModel(),
      quickFilter: (c.quickFilter as string) ?? '',
      filterTree: appliedTree, searchScope: appliedScope, proposalsFilter: appliedProposals,
      valueMode: appliedValueMode,
    });
    setColumnFilterCount(Object.keys(api.getFilterModel() ?? {}).length);
    setDirty(false);
    setPanel(null);
    liveColStateRef.current = api.getColumnState(); // snapshot the applied layout for width-restore
  };
  // Convergent default-view apply (§3.1). Re-runs whenever ANY precondition changes — gridReady,
  // the (async) views query, OR AG's onFirstDataRendered (via the firstDataRendered trigger) — so
  // the default lands whichever finishes LAST. gridReady (onGridReady) can precede column
  // INGESTION, and applyColumnState silently no-ops against colIds AG hasn't registered yet — the
  // real cause of a default-view miss. (The "grid mounts with empty colDefs" race does NOT occur:
  // the render gate holds AG off until rows+columns load.) So gate on api.getColumns() length, not
  // gridReady alone. Latch UNCONDITIONALLY once preconditions hold (even with no default) so a later
  // views refetch can't re-fire and clobber unsaved layout — but NEVER latch on an early bail.
  const [firstDataRendered, setFirstDataRendered] = useState(false);
  // §5.8: the grid renders but stays INVISIBLE (`.agWrapHolding`) until the resolved layout is on the
  // columns, so the operator's first frame isn't server column order followed by a visible reorder.
  const [holdingFirstFrame, markViewApplied] = useFirstFrameHold(views.isError);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    const api = apiRef.current;
    if (!api || !api.getColumns()?.length || !views.data) return;
    autoAppliedRef.current = true;
    // my own default first, else the list's admin-set TEAM default, else none (resolveDefaultView)
    const def = resolveDefaultView(views.data.views);
    if (def) applyView(def);
    markViewApplied(); // reveal — with or without a default, this IS the resolved layout
  }, [gridReady, views.data, firstDataRendered, markViewApplied]);
  // Width-restore: a colDef REBUILD (fieldOptions resolving, or an add/remove/refetch) re-asserts
  // defaultColDef.flex:1 and wipes applied fixed widths (order/sort/visibility survive; only width
  // regresses — the default-view "widths don't stick" bug). Re-apply width+flex from the live
  // snapshot AFTER each rebuild, restoring whatever was there (saved view OR an unsaved resize) — so
  // it never reverts the operator's in-progress layout. width/flex only (no applyOrder), so it can't
  // fight the order/insert-adjacency logic. Skip the initial colDefs (tryApplyDefault owns mount).
  useEffect(() => {
    if (colDefsInitRef.current) { colDefsInitRef.current = false; return; }
    const api = apiRef.current;
    const snap = liveColStateRef.current;
    if (!api || !snap) return;
    api.applyColumnState({ state: snap.map((s) => ({ colId: s.colId, width: s.width, flex: s.flex })) });
  }, [colDefs]);
  // derive dirty: does the current grid layout/filter/search diverge from the loaded view? (E3.5)
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !loadedView || baselineRef.current == null) { setDirty(false); return; }
    const key = viewConfigKey({
      columnState: api.getColumnState(), filterModel: api.getFilterModel(), quickFilter: search,
      filterTree, searchScope, proposalsFilter, valueMode,
    });
    setDirty(key !== baselineRef.current);
  }, [gridRev, search, loadedView, filterTree, searchScope, proposalsFilter, valueMode]);

  // Slice 9: keep the AG external-filter inputs in a ref (so the AG callbacks read the latest without
  // being re-created) and re-run the filter whenever any input changes.
  // Item 1d: does this list have proposed-record rows? Gates whether the [Current|AI] toggle
  // filters them (Current hides proposed rows in full; AI reveals them with projected values).
  const hasProposedRows = (grid.data?.proposed_count ?? 0) > 0;
  const extFilterRef = useRef({
    filterTree, proposalsFilter, searchScope, search, valueMode, hasProposedRows, columns: viewColumns,
  });
  useEffect(() => {
    extFilterRef.current = {
      filterTree, proposalsFilter, searchScope, search, valueMode, hasProposedRows, columns: viewColumns,
    };
    apiRef.current?.onFilterChanged();
  }, [filterTree, proposalsFilter, searchScope, search, valueMode, hasProposedRows, viewColumns]);
  const isExternalFilterPresent = useCallback(() => {
    const s = extFilterRef.current;
    return countConditions(s.filterTree) > 0 || s.proposalsFilter !== 'off'
      || (s.valueMode === 'current' && s.hasProposedRows)   // Item 1d: Current mode hides proposed rows
      || (s.searchScope === 'names' && s.search.trim() !== '');
  }, []);
  const doesExternalFilterPass = useCallback((node: { data?: Record<string, unknown> }) => {
    const s = extFilterRef.current;
    const row = node.data ?? {};
    // Item 1d (§6.1): the [Current|AI] toggle gates proposed-row VISIBILITY — Current mode shows
    // committed reality only; AI mode reveals proposed rows (rendered with their projected values).
    if (s.valueMode === 'current' && isProposedRecordRow(row)) return false;
    if (!evaluateFilterTree(s.filterTree, row, s.columns)) return false;
    // R3 (spec §5.3): the AI-Updates filter — 'new' = proposed-record rows, 'updated' = rows with
    // field proposals, 'all' = either. AND-ed with the other view filters below.
    if (s.proposalsFilter !== 'off') {
      const isNew = isProposedRecordRow(row);
      const isUpdated = !!row._proposals;
      if (s.proposalsFilter === 'new' && !isNew) return false;
      if (s.proposalsFilter === 'updated' && !isUpdated) return false;
      if (s.proposalsFilter === 'all' && !isNew && !isUpdated) return false;
    }
    if (s.searchScope === 'names' && s.search.trim()) {
      return cellText(row.name).toLowerCase().includes(s.search.trim().toLowerCase());
    }
    return true;
  }, []);
  // Keep a zero-result AI-Updates filter armed. It is saved query intent: a "Needs review" view
  // should begin matching again when proposals arrive. The active control remains visible below so
  // the operator can still turn the filter off explicitly.

  // R3 (spec §5.2/§5.5): row-DISTINCT AI-Updates counts. `scoped` = proposal rows passing the
  // current view; `total` = all loaded proposal rows; `hidden` = proposal rows the view filters out.
  // rowPassesView mirrors only the view's non-AI filters (filterTree + names-search) — it can't see
  // AG quick-filter / legacy column filterModel, so scoped counts can slightly over-state on those
  // views (the §5.5 caveat; self-heals with filter unification). Keyed to membership-changing inputs
  // only (NOT gridRev) so layout events don't trigger the O(rows) recount. `capped` = the fetch hit
  // FETCH_CAP so totals are a floor, not list-wide truth.
  const aiCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rowPassesView = (row: Record<string, unknown>) => {
      if (!evaluateFilterTree(filterTree, row, viewColumns)) return false;
      if (searchScope === 'names' && q) return cellText(row.name).toLowerCase().includes(q);
      return true;
    };
    let newTotal = 0, updatedTotal = 0, newScoped = 0, updatedScoped = 0, hidden = 0;
    for (const r of rows as Record<string, unknown>[]) {
      const isNew = isProposedRecordRow(r);
      const isUpd = !!r._proposals;
      if (!isNew && !isUpd) continue;
      const passes = rowPassesView(r);
      if (isNew) { newTotal++; if (passes) newScoped++; }
      if (isUpd) { updatedTotal++; if (passes) updatedScoped++; }
      if (!passes) hidden++;
    }
    return { newTotal, updatedTotal, newScoped, updatedScoped, hidden, capped: rows.length >= FETCH_CAP };
  }, [rows, filterTree, search, searchScope, viewColumns]);
  // AI-Updates control handlers: the primary button toggles off ↔ the last-used scope (default
  // 'all'); the ▾ menu sets an explicit scope. Both remember the scope for the next toggle.
  const toggleProposalsFilter = useCallback(() => {
    const next = proposalsFilter === 'off' ? lastProposalsScopeRef.current : 'off';
    setProposalsFilter(next);
    // Item 1d: a scope that includes New records needs AI mode, else Current-mode hides them and the
    // filter shows nothing.
    if (next === 'new' || next === 'all') setValueMode('ai');
  }, [proposalsFilter]);
  const setProposalsScope = useCallback((f: ProposalsFilter) => {
    if (f !== 'off') lastProposalsScopeRef.current = f;
    if (f === 'new' || f === 'all') setValueMode('ai'); // Item 1d — see toggleProposalsFilter
    setProposalsFilter(f);
  }, []);
  const showCurrentValues = useCallback(() => {
    setValueMode('current');
    setProposalsFilter((current) => {
      const next = cohereAiViewState(current, 'current').proposalsFilter;
      if (next !== 'off') lastProposalsScopeRef.current = next;
      return next;
    });
  }, []);

  const saveInPlace = () => {
    const config = captureConfig();
    if (!config || !loadedView) return;
    updateView.mutate({ viewId: loadedView.id, config }, {
      onSuccess: () => { baselineRef.current = viewConfigKey(config); setDirty(false); },
      onError: () => setEditError('Could not save the view — please try again.'),
    });
  };
  const doSaveAs = (name: string, shared: boolean) => {
    const config = captureConfig();
    if (!config) return;
    createView.mutate({ name, config, shared }, {
      // close the modal only on success — a failed save keeps it open with the error surfaced
      onSuccess: (v) => {
        setLoadedView(v); baselineRef.current = viewConfigKey(config); setDirty(false); setShowSaveAs(false);
      },
      onError: () => setEditError('Could not save the view — please try again.'),
    });
  };
  const updateViewToCurrent = (v: CrmView) => {
    const config = captureConfig();
    if (!config) return; // grid not ready — don't overwrite the saved view with an empty config
    updateView.mutate({ viewId: v.id, config, shared: v.shared }, {
      // if it's the loaded view, its saved state now matches the grid → clear the dirty indicator
      onSuccess: () => { if (v.id === loadedView?.id) { baselineRef.current = viewConfigKey(config); setDirty(false); } },
      onError: () => setEditError('Could not update the view.'),
    });
  };

  // Columns panel toggle: update `hidden` state (drives the checkbox) + push to the grid api.
  const toggleColumn = (field: string) => {
    const nowHidden = !hidden.has(field);
    const next = new Set(hidden);
    if (nowHidden) next.add(field);
    else next.delete(field);
    setHidden(next);
    apiRef.current?.setColumnsVisible([field], !nowHidden);
  };

  // Mirror the grid's column ORDER into state for the Columns panel. `gridRev` already bumps on
  // onColumnMoved / onColumnVisible / onColumnPinned / onSortChanged, so this re-reads after every
  // layout event (including a view load, which bumps it via applyColumnState's events) — and after a
  // colDef rebuild, hence the `columns` dep.
  //
  // ⚠ `firstDataRendered` is a dep for the same reason the default-view apply above carries it:
  // `gridReady` (onGridReady) can precede column INGESTION, and `getColumnState()` is empty until AG
  // has registered the columns. An empty `colOrder` is not cosmetic here — the panel treats it as
  // "the grid has not reported an order yet" and offers no drag handles at all, so without this dep a
  // lost race silently removes reordering for the session.
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !gridReady) return;
    const next = api.getColumnState().map((s) => s.colId).filter((c): c is string => !!c);
    setColOrder((prev) => (prev.length === next.length && prev.every((k, i) => k === next[i]) ? prev : next));
  }, [gridRev, gridReady, firstDataRendered, columns]);

  // Columns panel drag-to-reorder: apply the panel's new FULL order to the grid, exactly as the
  // Insert-left/right path does. Column order is view state — it rides in `columnState` and persists
  // through the SAME mutation a column-header drag does (updateView, via "Update to current view" /
  // Save view). The resulting onColumnMoved bumps gridRev, so the dirty indicator lights up and the
  // panel re-reads its order. `pinNameColumnState` re-stamps the identity column's pin, which is why
  // that column is not draggable in the panel: the grid would put it straight back.
  const reorderColumns = useCallback((orderedKeys: string[]) => {
    apiRef.current?.applyColumnState({
      state: pinNameColumnState(orderedKeys.map((colId) => ({ colId }))) as never,
      applyOrder: true,
    });
  }, []);

  // ── Item A: multi-sort model over the grid's column state ──
  // The grid (AG columnState) is the source of truth for sort — it already persists in the saved
  // view (columnState carries sort + sortIndex; crmViewConfig normalizes both). sortModel MIRRORS the
  // grid's live sort in state (priority-ordered), synced from the onSortChanged event's api (which is
  // post-render-safe, unlike a ref read during render). Drives the Sort panel + the toolbar count.
  const [sortModel, setSortModel] = useState<SortLevel[]>([]);
  // Apply the panel's ordered model as the grid's FULL sort: contiguous sortIndexes for the model's
  // columns, defaultState clears every other column's sort (also clears all when the model is empty).
  // The resulting sortChanged re-syncs sortModel + bumps gridRev (dirty detection).
  const applySortModel = useCallback((model: SortLevel[]) => {
    const api = apiRef.current;
    if (!api) return;
    api.applyColumnState({
      state: sortModelColumnState(model),
      defaultState: { sort: null, sortIndex: null },
    });
  }, []);

  // ── native-list editing (M3.1) ──
  const errMsg = (e: unknown, fallback: string) => {
    const detail = (e as { detail?: { detail?: string } } | null)?.detail?.detail;
    return typeof detail === 'string' ? detail : fallback;
  };
  const submitColumn = () => {
    const label = newCol.label.trim();
    const fieldKey = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80);
    if (!fieldKey) { setEditError('Enter a column name with letters or numbers.'); return; }
    setEditError(null);
    // M2: seed the picklist for an enum-family type from the options textarea (omit for others).
    const options = OPTION_TYPES.has(newCol.type) ? parseOptions(newCol.options) : undefined;
    const anchor = insertTarget; // capture: onSuccess fires after state may have changed
    addField.mutate(
      { field_key: fieldKey, label, field_type: newCol.type, ...(options?.length ? { options } : {}) },
      {
        onSuccess: () => {
          setNewCol({ label: '', type: 'text', options: '' });
          setInsertTarget(null);
          // if this add came from Insert-left/right, position the new column adjacent once it lands
          if (anchor) setPendingReorder({ newKey: fieldKey, anchorKey: anchor.fieldKey, side: anchor.side });
          setPanel(null);
        },
        onError: (e) => setEditError(errMsg(e, 'Could not add the column.')),
      },
    );
  };
  const saveOptions = () => {
    if (!optionsEdit) return;
    setEditError(null);
    setFieldOptions.mutate(
      { fieldKey: optionsEdit.fieldKey, options: parseOptions(optionsEdit.text) },
      {
        onSuccess: () => setOptionsEdit(null),
        onError: (e) => setEditError(errMsg(e, 'Could not save the options.')),
      },
    );
  };
  const onCellClicked = (e: { colDef: ColDef; data?: Record<string, unknown>; event?: Event | null }) => {
    const fk = e.colDef.field;
    if (!fk || !e.data) return;
    // R8: the name cell opens the record panel (via the name button in NameCellRenderer) — it must
    // NOT also trigger proposal review / inspect here, even if an AI proposal landed on `name`.
    if (fk === 'name') { setInspect(null); return; }
    // Clicking an interactive descendant (ref/note link, row-actions ⋯) is that control's action,
    // not a cell inspect — don't raise the bar for it (§6.2).
    const target = e.event?.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, [role="menuitem"]')) return;
    const prop = (e.data._proposals as Record<string, { value: unknown; proposal_id: number }> | undefined)?.[fk];
    const col = columns.find((c) => c.field_key === fk);
    if (prop) {
      // proposal cell → the accept/dismiss review bar (unchanged)
      setInspect(null);
      setReview({ entryId: Number(e.data.id), fieldKey: fk, label: col?.label ?? fk,
                  value: prop.value, current: e.data[fk], proposalId: prop.proposal_id });
      return;
    }
    // R4 (§6.2): non-proposal cell — show the full value in the inspector bar ONLY when its content
    // overflows the cell (so ordinary navigation on short cells doesn't flicker a bar open/closed).
    const cellEl = target?.closest('.ag-cell') as HTMLElement | null;
    const overflows = !!cellEl && cellContentOverflows(cellEl);
    setReview(null);
    setInspect(overflows ? { entryId: Number(e.data.id), fieldKey: fk, label: col?.label ?? fk } : null);
  };
  // The review bar stays valid only while fresh grid data still carries the EXACT proposal under
  // review (a background refetch may have superseded it with a newer run). Derived at render — no
  // stale bar ever shows, and Accept/Dismiss can't fire against a value the operator isn't seeing.
  const activeReview = useMemo(() => {
    if (!review) return null;
    const entry = rows.find((r) => Number((r as { id: unknown }).id) === review.entryId);
    const prop = (entry?._proposals as Record<string, { proposal_id: number }> | undefined)?.[review.fieldKey];
    return prop && prop.proposal_id === review.proposalId ? review : null;
  }, [rows, review]);
  // The inspector readout re-derives its value from fresh rows each render (stays current, and
  // vanishes if the row/field is gone). A proposal landing on the field promotes it to the review
  // bar instead (activeReview wins in the render below).
  const activeInspect = useMemo(() => {
    if (!inspect) return null;
    const entry = rows.find((r) => Number((r as { id: unknown }).id) === inspect.entryId);
    if (!entry) return null;
    const raw = (entry as Record<string, unknown>)[inspect.fieldKey];
    // W5 §10.3: the readout shows the DISPLAY vocabulary, so the inspector cannot contradict the
    // cell it was opened from — the third surface of review findings 8/12. A no-op for every column
    // without `enum_labels`, which is all of them but one today.
    const col = viewColumns.find((c) => c.field_key === inspect.fieldKey);
    return { label: inspect.label, value: col?.enum_labels ? enumDisplay(col, raw) : raw };
  }, [rows, inspect, viewColumns]);
  // R4 (§6.2): the bar self-dismisses — Escape (when not editing a cell / typing in a field), and a
  // pointerdown on neutral space (anything that isn't the bar itself or a grid cell; cell clicks go
  // through onCellClicked, which re-points or hides). Active only while a bar is open.
  useEffect(() => {
    if (!review && !inspect) return;
    const clear = () => { setReview(null); setInspect(null); };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT'
        || ae.isContentEditable || ae.closest('.ag-cell-inline-editing, .ag-popup-editor'))) return;
      clear();
    };
    const onDown = (ev: MouseEvent) => {
      const t = ev.target as HTMLElement | null;
      if (!t) return;
      if (barRef.current?.contains(t)) return;   // inside the bar → keep
      if (t.closest('.ag-cell')) return;          // a cell → onCellClicked decides (re-point/hide)
      clear();                                    // neutral space / chrome → hide
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [review, inspect]);

  const resolveReview = (action: 'accept' | 'dismiss') => {
    if (!activeReview) return;
    const mut = action === 'accept' ? acceptProposal : dismissProposal;
    mut.mutate({ entryId: activeReview.entryId, fieldKey: activeReview.fieldKey, expectedProposalId: activeReview.proposalId }, {
      onError: (err) => {
        // Two distinct 409s: stale (a newer aggregator run superseded what was rendered) vs
        // conflict:pending_edit (the operator has an un-synced edit on this field — their edit
        // leads). The invalidate on onSettled refetches fresh proposals either way.
        // ApiError.detail is the full response body; FastAPI nests our payload under `detail`
        // (HTTPException(409, detail=out)) → err.detail.detail.conflict.
        const e = err as { status?: number; detail?: { detail?: { conflict?: string } } } | null;
        if (e?.status === 409) {
          setEditError(e.detail?.detail?.conflict === 'pending_edit'
            ? 'You have an unsaved edit on this field — discard it first, then review the suggestion.'
            : 'This proposal was updated by a newer analysis — refreshed; please review again.');
        } else {
          setEditError(errMsg(err, `Could not ${action} the proposal.`));
        }
      },
      onSettled: () => setReview(null),
    });
  };

  const onCellEdit = (e: CellValueChangedEvent) => {
    // Slice 13: the pinned blank "add a record" row — committing its name creates the entry (opp lists)
    if (e.node.rowPinned === 'bottom') { commitNewRow(e); return; }
    if (!e.colDef.field || e.newValue === e.oldValue) return;
    // '' ↔ null are the same blank (the select editor uses '' for an empty cell) — not an edit
    if ((e.newValue === '' || e.newValue == null) && (e.oldValue === '' || e.oldValue == null)) return;
    // object/array cells (entity refs, multi_enum): AG-Grid compares by REFERENCE, so the ref editor
    // building a fresh array each keystroke makes a change-then-revert-to-same-set look edited. A
    // value compare skips that redundant PATCH (the backend is idempotent, but save the round-trip).
    if (typeof e.newValue === 'object' && typeof e.oldValue === 'object'
        && JSON.stringify(e.newValue) === JSON.stringify(e.oldValue)) return;
    setEditError(null);
    setFieldValue.mutate(
      { entryId: Number((e.data as { id: unknown }).id), field_key: e.colDef.field, value: e.newValue },
      {
        onError: (err) => setEditError(errMsg(err, 'Edit failed — the value was reverted.')),
      },
    );
  };

  // ── row-actions menu (opened from the name cell's ⋯) ──
  const openRowMenu = useCallback((entryId: number, anchorEl: HTMLElement) => {
    setRowMenu({ entryId, anchorEl });
  }, [setRowMenu]);
  const startEditName = (entryId: number) => {
    const api = apiRef.current;
    const node = api?.getRowNode(String(entryId));
    if (api && node?.rowIndex != null) api.startEditingCell({ rowIndex: node.rowIndex, colKey: 'name' });
  };
  const confirmDeleteEntry = (entryId: number) => {
    if (!window.confirm('Delete this record from the list?')) return;
    deleteEntry.mutate(entryId, { onError: (e) => setEditError(errMsg(e, 'Could not delete the record.')) });
  };

  // ── D8: whole-record proposal actions (opened from the proposed row's ⋯ menu) ──
  // The row bound to the open ⋯ menu + whether it's a whole-record proposal — gates the Accept/Dismiss
  // menu items (and mirrors the same predicate the full-row-green rowClassRules uses).
  const rowMenuRow = useMemo(
    () => (rowMenu ? rows.find((r) => Number((r as { id: unknown }).id) === rowMenu.entryId) ?? null : null),
    [rowMenu, rows]);
  const rowMenuProposed = isProposedRecordRow(rowMenuRow as Record<string, unknown> | null);
  const rowLabel = (row: Record<string, unknown> | null, entryId: number) =>
    cellText(row?.name) || `record #${entryId}`;
  // Accept = commit the proposed row as a real deal on this list. Consequential → confirm first.
  const acceptRecordFlow = useCallback((entryId: number, label: string) => {
    if (!window.confirm(
      `Accept “${label}” and commit it as a deal on this list? The reviewed field values are saved `
      + `to the record. Nothing is sent anywhere else.`)) return;
    setEditError(null);
    // Grid accept is the simple D8 promote (no per-field v2 review here → no record-level CAS echo).
    acceptRecord.mutate({ entryId }, {
      onError: (e) => setEditError(errMsg(e, 'Could not accept the record — the deal was not logged.')),
    });
  }, [acceptRecord]);
  const dismissRecordConfirm = (reason: DealDismissReason | FieldDismissReason, note?: string) => {
    if (!dismissRecordTarget) return;
    setEditError(null);
    dismissRecord.mutate(
      { entryId: dismissRecordTarget.entryId, reason, note },
      {
        onSuccess: () => setDismissRecordTarget(null),
        onError: (e) => setEditError(errMsg(e, 'Could not dismiss the record.')),
      },
    );
  };

  // ── Slice 13: inline record add via a pinned blank bottom row ──
  const committingRef = useRef(false); // double-submit guard (a fast Enter+Enter / blur+Enter)
  const resetPinnedName = () => {
    const node = apiRef.current?.getPinnedBottomRow(0);
    node?.setDataValue('name', null); // clear the add-row back to blank after a commit / no-op
  };
  const commitNewRow = (e: CellValueChangedEvent) => {
    // only the NAME cell creates a record — a stray edit to another editable pinned cell (e.g. a
    // currency field) must not create an entry named with that value. Revert the stray cell.
    if (e.colDef.field !== 'name') { e.node.setDataValue(e.colDef.field as string, null); return; }
    const name = String(e.newValue ?? '').trim();
    if (!name || committingRef.current) { resetPinnedName(); return; }
    committingRef.current = true;
    setEditError(null);
    addEntry.mutate(undefined, {
      onSuccess: (entry) => {
        const id = (entry as { id?: number } | undefined)?.id;
        resetPinnedName(); // free the add-row immediately for the next record
        if (id == null) { committingRef.current = false; return; }
        setFieldValue.mutate({ entryId: id, field_key: 'name', value: name }, {
          onError: (err) => setEditError(errMsg(err, 'Added the row, but naming it failed — edit it inline.')),
          onSettled: () => { committingRef.current = false; },
        });
      },
      onError: (err) => { committingRef.current = false; resetPinnedName(); setEditError(errMsg(err, 'Could not add the record.')); },
    });
  };
  const startPinnedAdd = useCallback(() => {
    if (subjectKind) { setPanel('addmember'); setMemberInput(''); setMemberQ(''); return; } // person/org → lookup dock
    const api = apiRef.current; // opportunity → edit the pinned name cell inline
    if (api) api.startEditingCell({ rowIndex: 0, rowPinned: 'bottom', colKey: 'name' });
  }, [subjectKind]);
  // one always-visible blank add-row on native lists (person/org + opportunity); none on Affinity-backed
  const pinnedBottomRows = useMemo(() => (isNative ? [{ id: NEW_ROW_ID }] : undefined), [isNative]);

  // ── column header actions (opened from the ⋮ in the custom header) ──
  const openFieldEdit = useCallback((field: EditField) => {
    setEditField(field);
    setConvertError(null);
    setPanel('editfield');
  }, [setEditField, setConvertError, setPanel]);
  const hideColumn = useCallback((fieldKey: string) => {
    setHidden((prev) => { const n = new Set(prev); n.add(fieldKey); return n; });
    apiRef.current?.setColumnsVisible([fieldKey], false);
  }, [setHidden]);
  const insertColumn = useCallback((fieldKey: string, side: 'left' | 'right') => {
    setInsertTarget({ fieldKey, side });
    setNewCol({ label: '', type: 'text', options: '' });
    setEditError(null);
    setPanel('addcol');
  }, [setInsertTarget, setNewCol, setPanel]);
  const deleteFieldAction = useCallback((fieldKey: string, label: string) => {
    // fetch the blast radius first so the confirm names how many cells lose their (recoverable) value
    fetchFieldUsage(listId, fieldKey)
      .then((u) => u.value_count)
      .catch(() => 0)
      .then((count) => {
        const msg = count > 0
          ? `Delete “${label}”? ${count} record${count === 1 ? '' : 's'} have a value — it’ll be hidden (recoverable).`
          : `Delete “${label}”? The column will be removed.`;
        if (!window.confirm(msg)) return;
        deleteField.mutate(fieldKey, {
          onError: (e) => setEditError(errMsg(e, 'Could not delete the field.')),
        });
      });
  }, [listId, deleteField]);
  const headerActions = useMemo<HeaderActions>(() => ({
    isNative, openFieldEdit, deleteField: deleteFieldAction, hideColumn, insertColumn,
  }), [isNative, openFieldEdit, deleteFieldAction, hideColumn, insertColumn]);
  const saveFieldLabel = (label: string) => {
    if (!editField) return;
    setEditError(null);
    updateField.mutate({ fieldKey: editField.fieldKey, label }, {
      onSuccess: () => { setEditField(null); setPanel(null); },
      onError: (e) => setEditError(errMsg(e, 'Could not rename the field.')),
    });
  };
  const convertFieldType = (newType: string) => {
    if (!editField) return;
    setConvertError(null);
    convertField.mutate({ fieldKey: editField.fieldKey, fieldType: newType }, {
      // success → the grid refetches with the new type; close the dock (editField's type is now stale)
      onSuccess: () => { setEditField(null); setPanel(null); },
      onError: (err) => {
        const e = err as { status?: number; detail?: { detail?: { conflict?: string; count?: number } } } | null;
        if (e?.status === 409 && e.detail?.detail?.conflict === 'multi_values') {
          const n = e.detail.detail.count ?? 0;
          setConvertError(`${n} row${n === 1 ? '' : 's'} hold more than one value — remove the extras first, then switch to single-select.`);
        } else {
          setConvertError(errMsg(err, 'Could not change the field type.'));
        }
      },
    });
  };
  // Slice 11 structured-options editor handlers (reuse convertError as the panel-scoped error line).
  // rename/delete return the mutation promise (mutateAsync) so the editor can roll back a failed
  // rename / prune only on a successful delete; the .catch sets the toast AND re-throws so the
  // editor's own .catch (rollback / keep-row) fires.
  const saveOptionConfig = (groups: { label: string; options: { value: string; color?: string }[] }[]) => {
    if (!editField) return;
    setConvertError(null);
    setFieldConfig.mutate({ fieldKey: editField.fieldKey, groups },
      { onError: (e) => setConvertError(errMsg(e, 'Could not save the options.')) });
  };
  const renameOptionValue = (from: string, to: string): Promise<void> => {
    if (!editField) return Promise.resolve();
    setConvertError(null);
    return renameOption.mutateAsync({ fieldKey: editField.fieldKey, from, to })
      .then(() => undefined)
      .catch((e) => { setConvertError(errMsg(e, `Could not rename “${from}”.`)); throw e; });
  };
  const deleteOptionValue = (value: string): Promise<void> => {
    if (!editField) return Promise.resolve();
    setConvertError(null);
    return deleteOption.mutateAsync({ fieldKey: editField.fieldKey, value })
      .then(() => undefined)
      .catch((err) => {
        const e = err as { status?: number; detail?: { detail?: { conflict?: string; count?: number } } } | null;
        if (e?.status === 409 && e.detail?.detail?.conflict === 'in_use') {
          const n = e.detail.detail.count ?? 0;
          setConvertError(`“${value}” is used by ${n} record${n === 1 ? '' : 's'} — reassign them first, then delete it.`);
        } else {
          setConvertError(errMsg(err, `Could not delete “${value}”.`));
        }
        throw err;
      });
  };

  // ── Item D (D1): the record-panel selection (rec param) opened OVER this list ──
  // rec = the crm_list_entry id of the open record; the panel renders from the already-loaded row
  // (R3). openRecord PUSHES a new selection (Back → grid) or, when a record is already open, REPLACES
  // it (rail click / prev-next conveyor — one Back-step for the whole browse). §7 tab-switch cleanup
  // is handled by TAB_PARAMS; sel-leaves-list cleanup by CrmPage.
  // The open/repoint/toggle semantics (§7.2 + §5.7c) live in `crmRecordSelection` — a leaf module, so
  // the regression tests exercise the shipped implementation rather than a replica of it.
  const { recEntryId, openRecord, closeRecord: recClose } = useRecordSelection(
    // one right dock at a time — opening a record closes any side dock
    useCallback(() => setPanel(null), []),
  );
  // Dock exclusion is BIDIRECTIONAL (§6.1): opening a side dock while a record is open closes the
  // record first (openRecord already closes docks the other way). Without this the dock is set but
  // render-gated to recEntryId==null → an invisible panel that pops open when the record closes.
  const openDock = useCallback((target: typeof panel) => {
    if (recEntryId != null) recClose();
    setPanel((p) => (p === target ? null : target));
  }, [recEntryId, recClose]);
  // the open record's row (from the loaded set); null → deep-linked/not-loaded → fallback shell.
  const recRow = useMemo(
    () => (recEntryId != null ? rows.find((r) => Number((r as { id: unknown }).id) === recEntryId) ?? null : null),
    [rows, recEntryId]);
  // last slot the open record occupied while it WAS in the displayed set — used to bracket a
  // nearest-neighbor when it later drops out of the filter (§6.2). Declared before recordNav (the memo
  // reads it synchronously during render).
  const lastGoodIndexRef = useRef(0);
  // RecordNavSource: the current filtered+sorted order (pinned add-row excluded) → position + prev/next.
  // Recomputes on data (rows) + sort/filter/data-rebuild (gridRev, bumped in onRowDataUpdated incl. the
  // initial load) + which record is open. `present:false` = the open record was filtered OUT of the
  // current set: keep the panel (recRow still loaded) but bracket the LAST-known slot so ‹ › jump to
  // the nearest survivor. An SSRM impl (D11/M2) swaps this one memo.
  const recordNav = useMemo(() => {
    if (recEntryId == null) return null;
    const api = apiRef.current;
    if (!api) return null;
    const ids: number[] = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.rowPinned) return; // exclude the pinned "add a record" row
      const rid = (node.data as { id?: unknown } | undefined)?.id;
      if (rid != null) ids.push(Number(rid));
    });
    const idx = ids.indexOf(recEntryId);
    if (idx >= 0) {
      return { present: true, index: idx, total: ids.length,
        prevId: idx > 0 ? ids[idx - 1] : null,
        nextId: idx < ids.length - 1 ? ids[idx + 1] : null };
    }
    // filtered out of the current set → bracket the last-known slot for a nearest-neighbor jump
    const anchor = ids.length ? Math.min(Math.max(lastGoodIndexRef.current, 0), ids.length - 1) : 0;
    return { present: false, index: -1, total: ids.length,
      prevId: ids.length && anchor > 0 ? ids[anchor - 1] : null,
      nextId: ids.length ? ids[anchor] ?? null : null };
  }, [recEntryId, gridRev, rows]);
  useEffect(() => { if (recordNav?.present) lastGoodIndexRef.current = recordNav.index; }, [recordNav]);
  // "Open full profile" from the panel → push sel=<kind>:<id>; CrmPage's §7 effect strips the now-
  // orphaned rec off the profile entry. (No history.back()+push race — a plain sel push.)
  const openRecordProfile = useCallback((kind: 'person' | 'organization', id: number) => {
    if (kind === 'person') openPerson(id); else openOrg(id);
  }, [openPerson, openOrg]);
  // F.8: ←/→ = previous/next record while a record is open. Lives in `crmRecordKeyNav` (a leaf
  // module) because the guard is the whole feature and it can only be tested with the REAL panel
  // mounted and focused — the version that shipped inline here never fired once.
  useRecordKeyNav(recEntryId, recordNav, openRecord);
  // F.6/F.7: the open record's row carries the active fill + accent, and the grid scrolls the minimum
  // needed to keep it in view. AG evaluates row class rules at ROW RENDER time, so the new rules
  // object alone leaves the outgoing row still highlighted — hence the targeted two-node redraw.
  //
  // ⚠ The REDRAW and the SCROLL are triggered differently on purpose (review finding). A rebuild
  // really can lose the class, so this re-runs on gridRev/gridReady as well as on a record change;
  // but gridRev ALSO bumps on sort, filter, column resize/move and every refetch, and scrolling on
  // those yanks the grid back to the active row after the operator deliberately scrolled away. That
  // decision lives inside `makeActiveRowSync`, deliberately NOT in this effect: the grid cannot be
  // mounted under jsdom, so logic left here is only ever testable against a hand-copied replica —
  // the trap `crmRecordSelection` was extracted to escape.
  const rowClassRules = useMemo(() => buildRowClassRules(recEntryId), [recEntryId]);
  const syncActiveRowRef = useRef(makeActiveRowSync());
  useEffect(() => {
    syncActiveRowRef.current(apiRef.current, recEntryId);
  }, [recEntryId, gridReady, gridRev]);

  // Item 2c (§7.3): click-away close. While a record is open, a pointerdown on NEUTRAL space closes
  // the panel. KEEP open for: the panel itself + any surface it spawned (`data-crm-panel-keepopen`),
  // the grid (`.ag-root-wrapper` — the pinned rail repoints, AG popups), portaled popovers
  // (`[role="menu"]`) / dialogs, and any interactive control (which runs its own handler — a nav
  // control's own navigation drops `rec` via TAB_PARAMS, so we must NOT recClose() first and race a
  // history.back()). Everything else — shell-header text, page chrome, empty space — closes.
  useEffect(() => {
    if (recEntryId == null) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t || t.closest('[data-crm-panel-keepopen], .ag-root-wrapper, [role="menu"], [role="dialog"],'
        + ' button, a, input, select, textarea, label')) return;
      recClose();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [recEntryId, recClose]);

  // grid context carries the row-open + row-menu + header-action callbacks into the (otherwise
  // isolated) cell + header components.
  const addLabel = subjectKind ? `Add ${subjectKind}` : 'Add a record';
  const gridContext = useMemo(
    () => ({ openPerson, openOrganization: openOrg, openNote, openRecord, openRowMenu, headerActions, startPinnedAdd, addLabel, valueMode, acceptRecord: acceptRecordFlow }),
    [openPerson, openOrg, openNote, openRecord, openRowMenu, headerActions, startPinnedAdd, addLabel, valueMode, acceptRecordFlow]);
  // R4 (§6.1): re-render cells when the display mode flips so the valueFormatter / custom renderers /
  // cellClassRules re-resolve current-vs-proposed. Force (the underlying values didn't change; only
  // the formatter output does). The context prop is already committed by the time this effect runs.
  useEffect(() => { apiRef.current?.refreshCells({ force: true }); }, [valueMode]);

  // after a column is added via Insert-left/right, place it next to its anchor once it lands in the
  // refetched schema (the create → invalidate → refetch cycle means the new colId isn't in the grid
  // at mutation-success time). Best-effort: if either colId is missing we just leave the new column
  // at the end — no data loss. Runs when `columns` changes (the refetch) with a pending reorder.
  useEffect(() => {
    if (!pendingReorder) return;
    const api = apiRef.current;
    const keys = new Set(columns.map((c) => c.field_key));
    if (!api || !keys.has(pendingReorder.newKey) || !keys.has(pendingReorder.anchorKey)) return;
    const order = api.getColumnState().map((s) => s.colId);
    const next = reorderColIds(order, pendingReorder.newKey, pendingReorder.anchorKey, pendingReorder.side);
    api.applyColumnState({ state: pinNameColumnState(next.map((colId) => ({ colId }))) as never, applyOrder: true });
    setPendingReorder(null);
  }, [columns, pendingReorder]);

  // close the dock + drop the transient edit/insert context so a stale anchor can't ride into the
  // next open (e.g. cancelling an Insert-left then adding a plain column).
  const closePanel = () => { setPanel(null); setInsertTarget(null); setEditField(null); setConvertError(null); };

  return (
    <div className={styles.listShell} ref={shellRef} style={shellHeight ? { height: shellHeight } : undefined}>
      <div className={styles.shellHeader}>
        <button className={styles.backButton} onClick={onBack}>← Lists</button>
        <h1 className={styles.shellTitle}>{grid.data?.list.name ?? 'List'}</h1>
        <span className={styles.subtitle}>
          native lists · {rows.length} row{rows.length === 1 ? '' : 's'} · parallel to Affinity
        </span>
        {/* list-sharing v1: shown only when the SERVER says this caller may manage sharing
            (owner / sharee / admin, and the list has an owner — a NULL-owner system list is
            firm-wide by class and never shareable, so no button). */}
        {grid.data?.list.can_share && (
          <button
            className={styles.gridPageBtn}
            onClick={() => setSharing(true)}
          >Share{(grid.data.list.shared_count ?? 0) > 0 ? ` · ${grid.data.list.shared_count}` : ''}</button>
        )}
        {/* R3 (§5.1): the record-level proposed count moved into the toolbar "AI Updates" control;
            the Lists-home row badge still shows it where there is no toolbar. */}
      </div>

      <div className={styles.gridToolbar}>
        {/* ── left group: query + shape the view ── */}
        <span className={styles.gridSearchWrap}>
          <input className={styles.gridSearch} type="search" placeholder={searchScope === 'names' ? 'Search record names…' : 'Search this list…'} value={search} onChange={(e) => setSearch(e.target.value)} />
          {/* §1.2: compact search-scope selector — an icon button inside the search field → 2-item menu */}
          <button
            className={styles.gridScopeBtn}
            aria-label={`Search scope: ${searchScope === 'names' ? 'record names' : 'anywhere'}`}
            aria-haspopup="menu"
            aria-expanded={scopeMenuAnchor != null}
            title="Search scope"
            onClick={(e) => setScopeMenuAnchor((a) => (a ? null : e.currentTarget))}
          >{searchScope === 'names' ? '⊙' : '✳'}<span className={styles.gridScopeCaret} aria-hidden="true">▾</span></button>
        </span>
        {scopeMenuAnchor && (
          <CrmPopover anchorEl={scopeMenuAnchor} onClose={() => setScopeMenuAnchor(null)} align="left">
            {(['anywhere', 'names'] as const).map((s) => (
              <button key={s} role="menuitemradio" aria-checked={searchScope === s} className={styles.popoverItem}
                onClick={() => { setSearchScope(s); setScopeMenuAnchor(null); }}>
                <span aria-hidden="true">{searchScope === s ? '● ' : '○ '}</span>{s === 'names' ? '⊙ Names' : '✳ Anywhere'}
              </button>
            ))}
          </CrmPopover>
        )}
        <button
          className={`${styles.gridPageBtn} ${panel === 'filter' ? styles.gridBtnActive : ''}`}
          disabled={!gridReady}
          title={`${countConditions(filterTree)} multi-filter condition(s) · ${columnFilterCount} in-grid column filter(s) · combined with AND`}
          onClick={() => openDock('filter')}
        >
          Filter{countConditions(filterTree) + columnFilterCount > 0 ? ` (${countConditions(filterTree) + columnFilterCount})` : ''}
        </button>
        {/* R3 (§5.1): AI Updates sits directly next to Filter — it IS a filter */}
        {((grid.data?.proposed_count ?? 0) > 0 || (grid.data?.proposals ?? 0) > 0 || proposalsFilter !== 'off') && (
          <CrmAiUpdatesControl
            filter={proposalsFilter}
            newCount={aiCounts.newScoped}
            updatedCount={aiCounts.updatedScoped}
            hiddenCount={aiCounts.hidden}
            newAvailable={(grid.data?.proposed_count ?? 0) > 0}
            updatedAvailable={(grid.data?.proposals ?? 0) > 0}
            capped={aiCounts.capped}
            onToggle={toggleProposalsFilter}
            onSetScope={setProposalsScope}
          />
        )}
        {/* R4 (§6.1) + Item 1d: the [Current|AI] toggle — fill IS the state (black = committed, green
            = AI). Current hides proposed rows + shows committed values; AI reveals proposed rows
            (projected values) + shows field-update proposals in green. Shown when EITHER exists. */}
        {((grid.data?.proposals ?? 0) > 0 || (grid.data?.proposed_count ?? 0) > 0 || valueMode === 'ai') && (
          <span className={styles.valueModeToggle} role="group" aria-label="Cell value display">
            <button
              className={`${styles.valueModeSeg} ${valueMode === 'current' ? styles.valueModeCurrentActive : ''}`}
              aria-pressed={valueMode === 'current'}
              title="Committed reality only — hide AI-proposed rows"
              onClick={showCurrentValues}
            >Current</button>
            <button
              className={`${styles.valueModeSeg} ${valueMode === 'ai' ? styles.valueModeAiActive : ''}`}
              aria-pressed={valueMode === 'ai'}
              title="Show AI-proposed rows + values (green)"
              onClick={() => setValueMode('ai')}
            >AI</button>
          </span>
        )}
        <button className={`${styles.gridPageBtn} ${panel === 'sort' ? styles.gridBtnActive : ''}`} disabled={!gridReady} onClick={() => openDock('sort')}>
          Sort{sortModel.length > 0 ? ` (${sortModel.length})` : ''}
        </button>
        <button className={`${styles.gridPageBtn} ${panel === 'views' ? styles.gridBtnActive : ''}`} disabled={!gridReady} onClick={() => openDock('views')}>
          Views{loadedView ? <span className={styles.gridViewName}> · {loadedView.name}</span> : null}{dirty ? ' •' : ''}
        </button>
        {/* §1.4: context-sensitive Save — primary is "Save" on an owned view, "Save a copy…"
            otherwise (never a dead primary); the ▾ caret offers "Save as new view…" on an owned view. */}
        <span className={styles.gridSaveWrap}>
          {loadedView?.is_mine ? (
            <button
              className={`${styles.gridPageBtn} ${styles.gridSavePrimary} ${dirty ? styles.gridSyncPending : ''}`}
              disabled={!gridReady || !dirty}
              title="Save changes to this view"
              onClick={saveInPlace}
            >{dirty ? 'Save •' : 'Save'}</button>
          ) : (
            <button
              className={`${styles.gridPageBtn} ${styles.gridSavePrimary}`}
              disabled={!gridReady}
              title={loadedView ? 'Team view — save the current layout as your own copy' : 'Save the current layout as a new view'}
              onClick={() => setShowSaveAs(true)}
            >Save a copy…</button>
          )}
          {loadedView?.is_mine && (
            <button
              className={`${styles.gridPageBtn} ${styles.gridSaveCaret}`}
              disabled={!gridReady}
              aria-label="More save options"
              aria-haspopup="menu"
              aria-expanded={saveMenuAnchor != null}
              onClick={(e) => setSaveMenuAnchor((a) => (a ? null : e.currentTarget))}
            >▾</button>
          )}
        </span>
        {saveMenuAnchor && (
          <CrmPopover anchorEl={saveMenuAnchor} onClose={() => setSaveMenuAnchor(null)} align="right">
            <button role="menuitem" className={styles.popoverItem}
              onClick={() => { setShowSaveAs(true); setSaveMenuAnchor(null); }}>Save as new view…</button>
          </CrmPopover>
        )}
        <span className={styles.gridToolbarSpacer} />
        {/* ── right group: structure / admin ── */}
        <button className={`${styles.gridPageBtn} ${panel === 'columns' ? styles.gridBtnActive : ''}`} disabled={!gridReady} onClick={() => openDock('columns')}>Columns</button>
        {/* Slice 13: record-add moved to the always-visible pinned blank row; only + Column stays here. */}
        {isNative && (
          <button className={`${styles.gridPageBtn} ${panel === 'addcol' ? styles.gridBtnActive : ''}`} onClick={() => { setInsertTarget(null); openDock('addcol'); }}>+ Column</button>
        )}
        {/* The overlay "sync dot" — click to drain a list's pending edits to Affinity — lived here.
            Deleted with `POST /api/crm/lists/{id}/sync` and `crm/push.py` by the Pillar-D write funeral
            (crm-of-record-plan §5, 2026-08-27). It had been a no-op button since the 2026-08-08 cut:
            `write_router='local'` made the drain skip, so it always reported 0 pushed. */}
      </div>

      {editError && (
        <div className={styles.gridEditError} role="alert" onClick={() => setEditError(null)}>
          {editError} <span className={styles.gridEditErrorX}>✕</span>
        </div>
      )}

      {/* R4 (§6.2): one bar, two modes — proposal review (accept/dismiss) OR a read-only value
          readout for an overflowing cell. Non-modal (no role="dialog"); Escape + click-away close
          it (effect above). barRef lets the click-away ignore clicks inside the bar. */}
      {(activeReview || activeInspect) && (
        <div ref={barRef}>
          {activeReview ? (
            <div className={styles.proposalBar} aria-label="Review AI proposal">
              <span className={styles.proposalBarText}>
                {/* The label row stays on one line; the VALUE wraps beneath it in full. The `AI`
                    eyebrow + green value are the only things that distinguish this from the ordinary
                    value readout below — one control, two states (owner, 2026-08-05). */}
                <span className={styles.proposalBarHead}>
                  <strong className={styles.proposalEyebrow}>AI</strong>
                  <span className={styles.proposalField}>{activeReview.label}</span>
                </span>
                {/* proposed value FIRST, struck current after (design-owner rule 2026-07-10: the
                    proposal is the payload; the prior value is context) */}
                <span className={styles.proposalValue} title="Proposed value">{cellText(activeReview.value) || '(blank)'}</span>
                {activeReview.current != null && activeReview.current !== '' && (
                  <span className={styles.proposalCurrent} title="Current value">{cellText(activeReview.current)}</span>
                )}
              </span>
              <span className={styles.proposalBarActions}>
                <button className={styles.proposalActionBtn} title="Accept" aria-label="Accept proposal" disabled={acceptProposal.isPending || dismissProposal.isPending} onClick={() => resolveReview('accept')}>✓</button>
                <button className={styles.proposalActionBtn} title="Dismiss" aria-label="Dismiss proposal" disabled={acceptProposal.isPending || dismissProposal.isPending} onClick={() => resolveReview('dismiss')}>×</button>
                <button className={styles.proposalActionBtn} title="Close (Esc)" aria-label="Close review bar" onClick={() => setReview(null)}>✕</button>
              </span>
            </div>
          ) : activeInspect ? (
            <div className={styles.proposalBar} aria-label="Cell value">
              <span className={styles.proposalBarText}>
                {/* Same shape as the AI variant above — no eyebrow, black value. */}
                <span className={styles.proposalBarHead}>
                  <span className={styles.proposalField}>{activeInspect.label}</span>
                </span>
                <span className={styles.inspectValue}>{cellText(activeInspect.value) || <span className={styles.gridEmpty}>—</span>}</span>
              </span>
              <span className={styles.proposalBarActions}>
                <button className={styles.proposalActionBtn} title="Close (Esc)" aria-label="Close value readout" onClick={() => setInspect(null)}>✕</button>
              </span>
            </div>
          ) : null}
        </div>
      )}

      <div className={styles.gridBody} ref={gridBodyRef}>
        {grid.isLoading && <div className={styles.loading}>Loading…</div>}
        {grid.isError && <div className={styles.error}>Failed to load the list.</div>}

        {!grid.isLoading && !grid.isError && (
          <div
            className={`${styles.agWrap}${holdingFirstFrame ? ` ${styles.agWrapHolding}` : ''}`}
            ref={agWrapRef}
          >
            <AgGridReact
              theme={emberTheme}
              rowData={rows}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              rowClassRules={rowClassRules}
              // Slice 9: quick-filter only when the search box is scoped to "anywhere"; in "names"
              // mode the box drives a name predicate in the external filter instead.
              quickFilterText={searchScope === 'anywhere' ? search : ''}
              // the filter builder + proposals-only + names-search compose as AG's external filter
              // (AND-ed with any per-column filters). extFilterRef keeps the callbacks non-stale.
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesExternalFilterPass}
              context={gridContext}
              // portal AG popups (column/filter menus, SetFilter, popup cell editors) to <body> so the
              // shell's overflow:hidden can't clip them and the docked side panel can't occlude them.
              popupParent={typeof document !== 'undefined' ? document.body : undefined}
              getRowId={(p) => String(p.data.id)}
              // Slice 13: always-visible blank "add a record" row (native lists only). Pinned rows are
              // excluded from sort/filter/selection by AG, so it never pollutes the data set.
              pinnedBottomRowData={pinnedBottomRows}
              animateRows
              suppressDragLeaveHidesColumns
              // keep the operator's column order (drag-reorder + a loaded view's order) across the
              // colDef rebuild that every mutation's refetch triggers — without this AG snaps columns
              // back to server/colDef order on setColumnDefs, discarding rename/delete/insert-time
              // arrangement (adv-review medium). Composes with the insert-adjacency reorder effect.
              maintainColumnOrder
              stopEditingWhenCellsLoseFocus
              onCellClicked={onCellClicked}
              onCellValueChanged={onCellEdit}
              onGridReady={(p) => { apiRef.current = p.api; setGridReady(true); }}
              // §3.1: onFirstDataRendered fires AFTER AG has ingested columnDefs (unlike onGridReady),
              // so it's the reliable "ready to accept column state" signal — flip the trigger the
              // default-view apply effect depends on so it re-checks its preconditions now.
              onFirstDataRendered={() => { setFirstDataRendered(true); updatePinnedWidth(); }}
              // Item 2b: keep the panel's --crm-pinned-w in sync with the pinned rail on any layout
              // or grid-size change.
              onGridSizeChanged={() => updatePinnedWidth()}
              // bump gridRev on layout changes so the dirty indicator re-derives (E3.5). Resize only
              // on `finished` (drag fires per-pixel). Filter changes flow through too.
              // keep sortModel (Sort panel + toolbar count) in lockstep with the grid; reading the
              // event's api post-render is safe (a ref read during render is not). Also feeds dirty.
              onSortChanged={(e) => { setGridRev((n) => n + 1); setSortModel(readSortModel(e.api.getColumnState())); }}
              onColumnMoved={() => { setGridRev((n) => n + 1); updatePinnedWidth(); }}
              onColumnVisible={() => { setGridRev((n) => n + 1); updatePinnedWidth(); }}
              onColumnPinned={() => { setGridRev((n) => n + 1); updatePinnedWidth(); }}
              onColumnResized={(e) => {
                if (!e.finished) return;
                setGridRev((n) => n + 1);
                updatePinnedWidth(); // Item 2b: name-col resize → panel edge tracks
                // E6.7: double-click-to-autosize must not blow one column out to fill the screen —
                // cap the autosized width at ⅔ of the grid width.
                if (e.source === 'autosizeColumns' && e.columns?.length) {
                  const w = agWrapRef.current?.clientWidth ?? 0;
                  const cap = Math.floor((w * 2) / 3);
                  const over = w > 0 ? e.columns.filter((col) => col.getActualWidth() > cap) : [];
                  if (over.length) e.api.setColumnWidths(over.map((col) => ({ key: col.getColId(), newWidth: cap })));
                }
                // Capture a USER-driven resize into the width snapshot so a later colDef rebuild
                // restores it (not the stale applied layout). Exclude programmatic sources ('flex' /
                // 'api'), else the flex-wipe from setColumnDefs would clobber the good snapshot.
                if (e.source === 'uiColumnResized' || e.source === 'autosizeColumns') {
                  liveColStateRef.current = e.api.getColumnState();
                }
              }}
              onFilterChanged={(e) => {
                setGridRev((n) => n + 1);
                setColumnFilterCount(Object.keys(e.api.getFilterModel() ?? {}).length);
              }}
              // force-refresh after a data update: AG-Grid's change detection skips cells whose
              // VALUE didn't change, but the _overlay pending marks (cellClassRules/tooltips) can
              // change with the value staying identical (adv-review) — e.g. a revert or a sync.
              onRowDataUpdated={(p) => {
                p.api.refreshCells({ force: true });
                // D1 review fix: AG rebuilds its filtered+sorted node model in a post-commit effect
                // and fires rowDataUpdated (NOT sort/filterChanged) — incl. on the INITIAL load. Bump
                // gridRev so the recordNav memo re-reads the fresh node order (else the ‹ › conveyor
                // lags a data rebuild by one cycle, and a deep-link-before-ready record has no
                // position/nav until an unrelated layout action). Loop-safe: rowData is unchanged on
                // the gridRev re-render, so this doesn't re-fire.
                setGridRev((n) => n + 1);
              }}
            />
          </div>
        )}

        {panel && recEntryId == null && (
          <CrmSidePanel
            title={
              panel === 'columns' ? 'Columns'
                : panel === 'views' ? 'Views'
                  : panel === 'filter' ? 'Filter'
                    : panel === 'sort' ? 'Sort'
                      : panel === 'editfield' ? 'Edit field'
                        : panel === 'addcol' ? (insertTarget ? 'Insert column' : 'Add column')
                          : `Add ${subjectKind ?? 'record'}`
            }
            onClose={closePanel}
          >
            {panel === 'filter' && (
              <>
                {columnFilterCount > 0 && (
                  <div className={styles.filterCompositionNote}>
                    <span>{columnFilterCount} in-grid column filter{columnFilterCount === 1 ? '' : 's'} also active. All filters combine with AND.</span>
                    <button className={styles.filterClear} onClick={() => apiRef.current?.setFilterModel(null)}>
                      Clear column filters
                    </button>
                  </div>
                )}
                <CrmFilterPanel columns={viewColumns} fieldOptions={viewFieldOptions} tree={filterTree} onChange={setFilterTree} />
              </>
            )}

            {panel === 'sort' && (
              <CrmSortPanel columns={viewColumns} model={sortModel} onChange={applySortModel} />
            )}

            {panel === 'editfield' && editField && (
              // key by field_key so switching Edit to another column while the dock is open REMOUNTS
              // the panel — else its internal label useState keeps the first field's value and Save
              // would rename the newly-selected column to the stale label (adv-review medium).
              <>
                <CrmFieldEditPanel
                  key={editField.fieldKey}
                  field={editField}
                  onSave={saveFieldLabel}
                  saving={updateField.isPending}
                  onConvertType={convertFieldType}
                  converting={convertField.isPending}
                  convertError={convertError}
                />
                {/* Slice 11: the structured options editor for enum-family fields on native lists */}
                {isNative && OPTION_TYPES.has(editField.fieldType) && (
                  <CrmOptionsEditor
                    key={`opts:${editField.fieldKey}`}
                    groups={(columns.find((c) => c.field_key === editField.fieldKey)?.groups) ?? null}
                    flatOptions={fieldOptions[editField.fieldKey] ?? []}
                    saving={setFieldConfig.isPending}
                    error={convertError}
                    onSetConfig={saveOptionConfig}
                    onRename={renameOptionValue}
                    onDelete={deleteOptionValue}
                  />
                )}
              </>
            )}

            {panel === 'addcol' && isNative && (
              <>
                <div className={styles.addColRow}>
                  <input
                    className={styles.setFilterSearch}
                    placeholder="Column name…"
                    value={newCol.label}
                    autoFocus
                    onChange={(e) => setNewCol((c) => ({ ...c, label: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitColumn(); }}
                  />
                  <select
                    className={styles.gridGroupBy}
                    value={newCol.type}
                    onChange={(e) => setNewCol((c) => ({ ...c, type: e.target.value }))}
                  >
                    {ADD_COLUMN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button className={styles.gridPageBtn} disabled={!newCol.label.trim() || addField.isPending} onClick={submitColumn}>Add</button>
                </div>
                {OPTION_TYPES.has(newCol.type) && (
                  <textarea
                    className={styles.optionsTextarea}
                    placeholder={`Options, one per line${newCol.type === 'ranked_enum' ? ' (top → bottom = rank order)' : ''}`}
                    value={newCol.options}
                    onChange={(e) => setNewCol((c) => ({ ...c, options: e.target.value }))}
                  />
                )}
              </>
            )}

            {panel === 'addmember' && isNative && subjectKind && (
              <>
                <div className={styles.addColRow}>
                  <input
                    className={styles.setFilterSearch}
                    placeholder={`Search the directory for a ${subjectKind}…`}
                    value={memberInput}
                    autoFocus
                    onChange={(e) => setMemberInput(e.target.value)}
                  />
                </div>
                <div className={styles.memberResults}>
                  {memberInput.trim().length < 2 && <span className={styles.gridEmpty}>Type at least 2 characters.</span>}
                  {memberInput.trim().length >= 2 && (memberLookup.data?.results.length ?? 0) === 0 && !memberLookup.isFetching && (
                    <span className={styles.gridEmpty}>No matches you can add.</span>
                  )}
                  {(memberLookup.data?.results ?? []).map((r) => (
                    <button
                      key={`${r.subject_kind}:${r.subject_id}`}
                      className={styles.memberResult}
                      disabled={addMember.isPending}
                      onClick={() => addMember.mutate(
                        { subject_kind: r.subject_kind, subject_id: r.subject_id },
                        {
                          onSuccess: (out) => setEditError(out.duplicate ? `${out.name} is already on this list.` : null),
                          onError: (e) => setEditError(errMsg(e, `Could not add ${r.name}.`)),
                        },
                      )}
                    >
                      + {r.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {panel === 'columns' && (
              <CrmColumnsPanel
                columns={viewColumns}
                hidden={hidden}
                onToggle={toggleColumn}
                showOptionsEditor={editMode === 'direct'}
                fieldOptions={fieldOptions}
                optionsEdit={optionsEdit}
                setOptionsEdit={setOptionsEdit}
                onSaveOptions={saveOptions}
                savingOptions={setFieldOptions.isPending}
                onNewField={isNative ? () => setPanel('addcol') : undefined}
                availableDerived={availableDerived}
                onSetDerived={isNative ? (keys) => {
                  setEditError(null);
                  setListDerived.mutate(keys, { onError: (e) => setEditError(errMsg(e, 'Could not update graph columns.')) });
                } : undefined}
                derivedSaving={setListDerived.isPending}
                // W5 §10.1: the server-owned catalog drives the System-fields groups. Unlike
                // `available_derived` (subject-kind keyed, and therefore EMPTY on the IOT) this
                // carries the record and AI-review families too, plus each field's availability and
                // definition — so the panel offers exactly what the list can serve.
                availableSystemFields={availableSystemFields}
                // ⛔ `canSetTeamDefault` is NOT passed here. It answers "may this caller move the
                // LIST's team default" (require_admin); the panel's "on for this list" toggle writes
                // `PATCH /lists/{id}/derived`, which is require_crm_privileged — the owner and every
                // sharee. Feeding the views-admin flag in conflated the two and hid an owner's own
                // control from the owner. The panel derives its own eligibility from `onSetDerived`
                // + `availableDerived`, which is exactly the door it calls.
                columnOrder={colOrder}
                onReorder={reorderColumns}
                pinnedKeys={PINNED_COLUMN_KEYS}
              />
            )}

            {panel === 'views' && (
              <CrmViewsPanel
                views={views.data?.views ?? []}
                loadedViewId={loadedView?.id ?? null}
                onLoad={applyView}
                onSetDefault={(v) => updateView.mutate({ viewId: v.id, is_default: true })}
                onUpdateToCurrent={updateViewToCurrent}
                onRename={(v, name) => {
                  setEditError(null);
                  updateView.mutate({ viewId: v.id, name },
                    { onError: (e) => setEditError(errMsg(e, `Could not rename “${v.name}”.`)) });
                }}
                onToggleShared={(v) => {
                  setEditError(null);
                  // un-sharing THE team default is refused by name server-side ("clear the team
                  // default first") — surface that detail rather than failing silently.
                  updateView.mutate({ viewId: v.id, shared: !v.shared },
                    { onError: (e) => setEditError(errMsg(e, 'Could not update the view.')) });
                }}
                onDelete={(v) => { if (window.confirm(`Delete view "${v.name}"?`)) deleteView.mutate(v.id); }}
                // admin-only affordance; `undefined` for everyone else is what hides it (the server's
                // require_admin is the authority — this only keeps a doomed request off the wire)
                onSetTeamDefault={canSetTeamDefault ? (v, next) => {
                  setEditError(null);
                  setTeamDefaultView.mutate(next ? v.id : null,
                    { onError: (e) => setEditError(errMsg(e, 'Could not set the team default view.')) });
                } : undefined}
              />
            )}
          </CrmSidePanel>
        )}

        {/* Item D: the record detail panel — a wide right-dock over the grid body; the pinned name
            column stays visible at the left as the nav rail. The container hydrates the authoritative
            record_field_states.v1 payload (D2b); a not-in-loaded-set deep link (recRow == null) is
            fetched by the endpoint rather than dead-ending (§6.5). */}
        {recEntryId != null && (
          <CrmRecordPanelContainer
            listId={listId}
            entryId={recEntryId}
            record={(recRow as Record<string, unknown>) ?? undefined}
            columns={columns}
            position={recordNav?.present ? { index: recordNav.index + 1, total: recordNav.total } : null}
            staleInView={!!recordNav && !recordNav.present}
            hasPrev={recordNav?.prevId != null}
            hasNext={recordNav?.nextId != null}
            onPrev={() => { if (recordNav?.prevId != null) openRecord(recordNav.prevId); }}
            onNext={() => { if (recordNav?.nextId != null) openRecord(recordNav.nextId); }}
            onClose={recClose}
            onOpenProfile={openRecordProfile}
            onOpenMsg={openMsg}
          />
        )}
      </div>

      {rowMenu && (
        <CrmPopover anchorEl={rowMenu.anchorEl} onClose={() => setRowMenu(null)}>
          <RowActionsMenu
            isNative={isNative}
            isOpportunity={subjectKind === null}
            isProposed={rowMenuProposed}
            onOpen={() => { const id = rowMenu.entryId; setRowMenu(null); openRecord(id); }}
            onEditName={() => { const id = rowMenu.entryId; setRowMenu(null); startEditName(id); }}
            onDelete={() => { const id = rowMenu.entryId; setRowMenu(null); confirmDeleteEntry(id); }}
            onAccept={() => {
              const id = rowMenu.entryId; const label = rowLabel(rowMenuRow, id);
              setRowMenu(null); acceptRecordFlow(id, label);
            }}
            onDismiss={() => {
              const id = rowMenu.entryId; const label = rowLabel(rowMenuRow, id);
              setRowMenu(null); setDismissRecordTarget({ entryId: id, label });
            }}
          />
        </CrmPopover>
      )}

      {/* D8: whole-record dismiss reuses the Deals-tab reason taxonomy (new-deal reasons, since a
          proposed record is a candidate). Transient modal — kept in useState, not URL state (§nav). */}
      <DismissReasonModal
        open={dismissRecordTarget != null}
        granularity="deal"
        dealKind="candidate"
        subjectLabel={dismissRecordTarget?.label ?? ''}
        submitting={dismissRecord.isPending}
        onClose={() => setDismissRecordTarget(null)}
        onConfirm={dismissRecordConfirm}
      />

      {showSaveAs && (
        <CrmViewSaveModal onClose={() => setShowSaveAs(false)} saving={createView.isPending} onSave={doSaveAs} />
      )}

      {/* list-sharing v1. A transient modal, so `useState` and NOT a URL param (the navigation
          spec's rule: only persistent drill-downs deep link). `onLeft` fires when the caller removed
          THEMSELVES — they can no longer read this list, so we return them to the Lists home rather
          than leaving a grid on screen whose next fetch 404s. */}
      {sharing && (
        <CrmShareDialog
          listId={listId}
          listName={grid.data?.list.name ?? 'this list'}
          onClose={() => setSharing(false)}
          onLeft={onBack}
        />
      )}
    </div>
  );
}
